import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiEdit, FiUpload, FiDownload, FiTrash2, FiCheck, FiX, FiEye, FiVideo, FiPlus, FiLink, FiCalendar, FiDollarSign, FiRefreshCw, FiPlay, FiFileText, FiCopy } from 'react-icons/fi';
import { studentsAPI, activitiesAPI, meetingsAPI, documentsAPI, applicationsAPI, settingsAPI, interviewsAPI, mockInterviewsAPI, feesAPI, refundsAPI, zoomAPI, jobOpeningsAPI } from '../../services/api';
import EmailThreadPanel from '../../components/Common/EmailThreadPanel';
import StatusBadge from '../../components/Common/StatusBadge';
import Modal from '../../components/Common/Modal';
import FranceInterviewOutcomeModal from '../../components/Common/FranceInterviewOutcomeModal';
import { isFranceCampusName } from '../../utils/campus';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { getTodayDateInputMin, isDateTimeLocalAtLeastHoursInFuture, isDateTimeLocalInFuture, storedDatetimeToLocalInput, formatStoredDateTime } from '../../utils/futureDateTime';
import { getBrowserIanaTimezone, resolveDefaultInterviewTimezone } from '../../utils/browserTimezone';
import {
  formatInterviewSlotAvailabilitySuffix,
  canBypassInterviewSlotCapacity
} from '../../utils/interviewSlotAvailabilityLabel';
import AuditTimeline from '../../components/Common/AuditTimeline';

import { getDocumentFileSizeError } from '../../utils/uploadLimits';
import { EntityDocumentGenerator } from '../../components/Common/DocumentGenerator';
import { canAccessStudentLetterGeneration } from '../../utils/letterGenerationAccess';
import { useCountry } from '../../context/CountryContext';
import { isEUNationality } from '../../config/crmConfig';
import { useTranslation } from 'react-i18next';

function formatProgramTypeForDetail(raw) {
  if (raw == null || raw === '') return '-';
  const s = String(raw).trim();
  const lower = s.toLowerCase().replace(/\s+/g, '_');
  const map = {
    foundation: 'Foundation',
    undergraduate: "Bachelor's",
    postgraduate: "Master's",
    diploma: 'Diploma',
    certificate: 'Certificate',
    phd: 'PhD'
  };
  return map[lower] || s;
}

function formatFeeTypeLabel(feeTypeKey) {
  if (feeTypeKey == null || feeTypeKey === '') return '-';
  return String(feeTypeKey)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function HistoryDetails({ title, logs, filterLog }) {
  return (
    <div style={{ marginTop: 12 }}>
      <details>
        <summary
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontWeight: 700,
            color: 'var(--text)',
            fontSize: 13
          }}
        >
          {title}
        </summary>
        <div style={{ marginTop: 10 }}>
          <AuditTimeline
            logs={logs}
            showEntity={false}
            maxCollapsedChanges={3}
            filterLog={filterLog}
          />
        </div>
      </details>
    </div>
  );
}

const getFileNameFromPath = (pathValue) => {
  if (!pathValue) return '';
  const parts = String(pathValue).split('/');
  return parts[parts.length - 1] || pathValue;
};

const StudentDetail = () => {
  const { t } = useTranslation();
  const { canPerformAction, hasModuleAccess, user: currentUser } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const canViewCompanyStatus = isFranceCRM && hasModuleAccess('student_company_status');
  const canEditCompanyStatus = canPerformAction('student_company_status', 'update');
  const countryConfig = getConfig();
  const feeTypesFromConfig = useMemo(
    () => countryConfig?.features?.feeTypes || ['application_fee', 'tuition_fee', 'registration_fee', 'other'],
    [countryConfig]
  );
  const defaultFeeType = feeTypesFromConfig[0] || 'application_fee';
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [student, setStudent] = useState(null);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [mockInterviews, setMockInterviews] = useState([]);
  const [fees, setFees] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [feesSummary, setFeesSummary] = useState({ total_due: 0, total_paid: 0, outstanding: 0 });
  const [auditLog, setAuditLog] = useState([]);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [companyStatusOptions, setCompanyStatusOptions] = useState([]);
  const [updatingCompanyStatus, setUpdatingCompanyStatus] = useState(false);

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [reschedulingInterview, setReschedulingInterview] = useState(null);
  const [showMockRescheduleModal, setShowMockRescheduleModal] = useState(false);
  const [mockRescheduleLoading, setMockRescheduleLoading] = useState(false);
  const [reschedulingMockInterview, setReschedulingMockInterview] = useState(null);
  const [zoomInterviewSyncingId, setZoomInterviewSyncingId] = useState(null);
  const [zoomMockSyncingId, setZoomMockSyncingId] = useState(null);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomMockGenerating, setZoomMockGenerating] = useState(false);
  const [zoomInterviewGenerating, setZoomInterviewGenerating] = useState(false);
  const [franceInterviewOutcomeDialog, setFranceInterviewOutcomeDialog] = useState(null);
  /** null = loading; controls Letters tab visibility on student profile */
  const [letterAccessConfig, setLetterAccessConfig] = useState(null);
  const [interviewTimezones, setInterviewTimezones] = useState([]);
  const [rescheduleForm, setRescheduleForm] = useState({
    scheduled_at: '',
    timezone: getBrowserIanaTimezone(),
    interview_link: ''
  });
  const [rescheduleSlotDate, setRescheduleSlotDate] = useState('');
  const [rescheduleSlotId, setRescheduleSlotId] = useState('');
  const [desiredRescheduleSlotTime, setDesiredRescheduleSlotTime] = useState('');
  const [rescheduleSlotOptions, setRescheduleSlotOptions] = useState([]);
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);

  const [mockRescheduleSlotDate, setMockRescheduleSlotDate] = useState('');
  const [mockRescheduleSlotId, setMockRescheduleSlotId] = useState('');
  const copyToClipboard = useCallback(async (text, kind) => {
    const value = String(text || '').trim();
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(kind === 'phone' ? 'Phone number copied' : 'Email copied');
    } catch (_) {
      toast.error('Copy failed');
    }
  }, []);
  const [desiredMockRescheduleSlotTime, setDesiredMockRescheduleSlotTime] = useState('');
  const [mockRescheduleSlotOptions, setMockRescheduleSlotOptions] = useState([]);
  const [mockRescheduleSlotsLoading, setMockRescheduleSlotsLoading] = useState(false);

  const scheduledAtFromSlot = (dateStr, hhmm) => {
    if (!dateStr || !hhmm) return '';
    return `${String(dateStr).slice(0, 10)}T${String(hhmm).slice(0, 5)}`;
  };
  const [appFormLoading, setAppFormLoading] = useState(false);
  const [appFormDropdownOpen, setAppFormDropdownOpen] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [editingRefund, setEditingRefund] = useState(null);
  const [feeSaving, setFeeSaving] = useState(false);
  const [refundSaving, setRefundSaving] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'Call', activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10), activity_time: '', next_followup: '', notes: '' });
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    custom_document_type: '',
    document_category: 'documents',
    file: null,
    notes: ''
  });
  const [documentTypes, setDocumentTypes] = useState([]);
  const [feeForm, setFeeForm] = useState({
    fee_type: defaultFeeType,
    description: '',
    currency: 'EURO',
    amount_paid: '',
    payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
    payment_method: '',
    reference_no: '',
    payment_receipt_copy: null,
    payment_receipt_copy_name: '',
    notes: ''
  });
  const feeTypeSelectOptions = useMemo(() => {
    const base = [...feeTypesFromConfig];
    if (!base.includes('registration_fee')) {
      base.push('registration_fee');
    }
    if (!base.includes('visa_fee')) {
      base.push('visa_fee');
    }
    if (feeForm.fee_type && !base.includes(feeForm.fee_type)) {
      base.push(feeForm.fee_type);
    }
    return base;
  }, [feeTypesFromConfig, feeForm.fee_type]);
  const [refundForm, setRefundForm] = useState({
    refund_type: 'application_fee',
    description: '',
    currency: 'EURO',
    amount_refunded: '',
    refund_method: '',
    reference_no: '',
    refund_receipt_copy: null,
    refund_receipt_copy_name: '',
    notes: ''
  });
  const [appForm, setAppForm] = useState({});
  const [visaForm, setVisaForm] = useState({ visa_status: '', visa_result: '' });
  const [visaStatusOptions, setVisaStatusOptions] = useState([]);
  const [visaResultOptions, setVisaResultOptions] = useState([]);
  const [visaSaving, setVisaSaving] = useState(false);
  const [enrollmentInput, setEnrollmentInput] = useState('');
  const [enrollmentSaving, setEnrollmentSaving] = useState(false);

  // Imperative handle to the Payment Receipts generator so per-row
  // "Generate Receipt" buttons in the Fees table can trigger generation
  // scoped to a specific student_fees row.
  const paymentReceiptRef = useRef(null);

  const loadStudent = useCallback(async () => {
    try {
      const [stuRes, actRes, meetRes, intRes, mockIntRes, feesRes, refundsRes, auditRes, jobsRes] = await Promise.all([
        studentsAPI.getById(id),
        activitiesAPI.getCombined(id),
        meetingsAPI.getAll({ student_id: id }).catch(() => ({ data: { meetings: [] } })),
        interviewsAPI.getByStudent(id).catch(() => ({ data: [] })),
        mockInterviewsAPI.getByStudent(id).catch(() => ({ data: [] })),
        feesAPI.getByStudent(id).catch(() => ({ data: { fees: [], summary: { total_due: 0, total_paid: 0, outstanding: 0 } } })),
        refundsAPI.getByStudent(id).catch(() => ({ data: { refunds: [] } })),
        settingsAPI.getAuditLog({ student_id: id, include_related: 1, limit: 200 }).catch(() => ({ data: [] })),
        jobOpeningsAPI.getByStudent(id).catch(() => ({ data: { jobs: [] } }))
      ]);
      setStudent(stuRes.data);
      const enr = stuRes.data?.enrollment_number;
      setEnrollmentInput(enr != null && String(enr).trim() !== '' ? String(enr).trim() : '');
      setVisaForm({
        visa_status: stuRes.data?.visa_status || '',
        visa_result: stuRes.data?.visa_result || ''
      });
      setActivities(actRes.data);
      setMeetings(meetRes.data.meetings);
      setInterviews(intRes.data || []);
      setMockInterviews(mockIntRes.data || []);
      setFees(feesRes.data.fees || []);
      setRefunds(refundsRes.data.refunds || []);
      setFeesSummary(feesRes.data.summary || { total_due: 0, total_paid: 0, outstanding: 0 });
      setAuditLog(auditRes.data);
      setJobOpenings(jobsRes?.data?.jobs || []);
      if (stuRes.data.applications && stuRes.data.applications.length > 0) {
        const app = stuRes.data.applications[0];
        setAppForm({
          ...app,
          app_date: app.app_date || (app.created_at ? String(app.created_at).slice(0, 10) : ''),
          application_fee_applicable: app.application_fee !== null && app.application_fee !== undefined && app.application_fee !== ''
        });
      }
    } catch (err) {
      toast.error(t('studentDetail.failedLoad'));
      navigate('/students');
    }
    setLoading(false);
  }, [id, navigate]);

  const handleResumeAutoStatus = async () => {
    try {
      await studentsAPI.update(id, { resume_auto_status: true });
      toast.success(t('studentDetail.resumeAutoStatusSuccess'));
      await loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('common.failed'));
    }
  };

  const handleCompanyStatusUpdate = async (nextCompanyStatus) => {
    try {
      setUpdatingCompanyStatus(true);
      const value = nextCompanyStatus ? String(nextCompanyStatus) : null;
      await studentsAPI.updateCompanyStatus(id, { company_status: value });
      setStudent((prev) => (prev ? { ...prev, company_status: value } : prev));
      toast.success(t('studentDetail.companyStatusUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('students.companyStatusUpdateFailed'));
    } finally {
      setUpdatingCompanyStatus(false);
    }
  };

  useEffect(() => { loadStudent(); }, [loadStudent]);

  useEffect(() => {
    if (!isFranceCRM) {
      setCompanyStatusOptions([]);
      return;
    }
    settingsAPI.getStudentCompanyStatuses()
      .then((res) => {
        setCompanyStatusOptions(
          (res.data || []).filter((s) => s.status === 'active').map((s) => s.status_name)
        );
      })
      .catch(() => setCompanyStatusOptions([]));
  }, [isFranceCRM]);

  useEffect(() => {
    if (!appFormDropdownOpen) return;
    const close = () => setAppFormDropdownOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [appFormDropdownOpen]);

  useEffect(() => {
    Promise.all([
      settingsAPI.getDocumentTypes({ category: 'documents' }).catch(() => ({ data: [] })),
      settingsAPI.getDocumentTypes({ category: 'visa_documents' }).catch(() => ({ data: [] })),
      settingsAPI.getInterviewTimezones().catch(() => ({ data: [] })),
      settingsAPI.getVisaStatuses().catch(() => ({ data: [] })),
      settingsAPI.getVisaResults().catch(() => ({ data: [] }))
    ]).then(([normal, visa, timezones, visaStatuses, visaResults]) => {
      setDocumentTypes([...(normal.data || []), ...(visa.data || [])].filter(d => d.status === 'active'));
      const activeTimezones = (timezones.data || []).filter((tz) => tz.status === 'active');
      setInterviewTimezones(activeTimezones);
      setVisaStatusOptions((visaStatuses.data || []).filter((s) => String(s.status) === 'active'));
      setVisaResultOptions((visaResults.data || []).filter((r) => String(r.status) === 'active'));
      if (activeTimezones.length > 0) {
        setRescheduleForm((prev) => ({ ...prev, timezone: resolveDefaultInterviewTimezone(activeTimezones) }));
      }
    });
    zoomAPI.getStatus().then((r) => setZoomConfigured(r.data?.configured === true)).catch(() => {});
  }, []);

  const handleLogActivity = async () => {
    try {
      await activitiesAPI.create({ ...activityForm, student_id: id });
      toast.success(t('studentDetail.toasts.activityLogged'));
      setShowActivityModal(false);
      setActivityForm({ activity_type: 'Call', activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10), activity_time: '', next_followup: '', notes: '' });
      loadStudent();
    } catch (err) { toast.error(err.response?.data?.message || t('common.failed')); }
  };

  const isOtherDocumentType = (value) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return ['other', 'others', 'autre', 'autres'].includes(normalized);
  };

  const handleUpload = async () => {
    const isOtherType = isOtherDocumentType(uploadForm.document_type);
    const finalDocumentType = isOtherType
      ? String(uploadForm.custom_document_type || '').trim()
      : String(uploadForm.document_type || '').trim();
    if (!finalDocumentType) {
      toast.error(t('studentDetail.modals.docType'));
      return;
    }
    if (!uploadForm.file) { toast.error(t('studentDetail.documents.selectFile')); return; }
    const sizeErr = getDocumentFileSizeError(uploadForm.file);
    if (sizeErr) { toast.error(sizeErr); return; }
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('document_type', finalDocumentType);
    formData.append('document_category', uploadForm.document_category || 'documents');
    formData.append('notes', uploadForm.notes);
    try {
      setUploadingDocument(true);
      await documentsAPI.upload(id, formData);
      toast.success(t('studentDetail.documents.uploadSuccess'));
      setShowUploadModal(false);
      setUploadForm({
        document_type: '',
        custom_document_type: '',
        document_category: activeTab === 'visa-documents' ? 'visa_documents' : 'documents',
        file: null,
        notes: ''
      });
      loadStudent();
    } catch (err) { toast.error(err.response?.data?.message || t('studentDetail.documents.uploadFailed')); }
    finally { setUploadingDocument(false); }
  };

  const handleViewDoc = async (docId) => {
    try {
      const res = await documentsAPI.getUrl(docId);
      window.open(res.data.url, '_blank');
    } catch (err) { toast.error(t('studentDetail.documents.urlFailed')); }
  };

  const handleDownloadDoc = (docId) => {
    documentsAPI.download(docId);
  };

  const downloadApplicationForm = async (lang) => {
    setAppFormLoading(true);
    setAppFormDropdownOpen(false);
    try {
      const res = await studentsAPI.getApplicationFormPdf(id, lang);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (res.headers['content-type'] || '').includes('pdf') ? 'pdf' : 'docx';
      const suffix = lang === 'fr' ? '_FR' : '';
      a.download = `Application_Form${suffix}_${student.student_ref || id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error(t('studentDetail.toasts.appFormFailed'));
    } finally {
      setAppFormLoading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm(t('studentDetail.documents.deleteConfirm'))) return;
    try {
      await documentsAPI.delete(docId);
      toast.success(t('studentDetail.documents.deleteSuccess'));
      loadStudent();
    } catch (err) { toast.error(t('studentDetail.documents.deleteFailed')); }
  };

  const handleSaveFee = async () => {
    if (!feeForm.fee_type || !feeForm.currency || feeForm.amount_paid === '' || !feeForm.payment_date) {
      toast.error(t('studentDetail.fees.fillRequired'));
      return;
    }

    const amountPaid = parseFloat(feeForm.amount_paid);
    if (isNaN(amountPaid) || amountPaid < 0) {
      toast.error(t('studentDetail.fees.invalidAmount'));
      return;
    }
    const hasNewReceiptFile = feeForm.payment_receipt_copy instanceof File;
    const hasExistingReceiptFile = Boolean(editingFee?.payment_receipt_copy);
    if (!hasNewReceiptFile && !hasExistingReceiptFile) {
      toast.error(t('studentDetail.fees.receiptRequired'));
      return;
    }
    if (feeForm.payment_receipt_copy instanceof File) {
      const sizeErr = getDocumentFileSizeError(feeForm.payment_receipt_copy);
      if (sizeErr) { toast.error(sizeErr); return; }
    }

    const editingFeeId = editingFee?.fee_id || null;
    if (feeForm.fee_type === 'application_fee') {
      if (!appForm.application_fee_applicable || configuredApplicationFee <= 0) {
        toast.error(t('studentDetail.fees.appFeeNotConfigured'));
        return;
      }
      const alreadyPaid = getPaidByType('application_fee', editingFeeId);
      const maxAllowed = configuredApplicationFee;
      const remainingAllowed = Math.max(0, maxAllowed - alreadyPaid);
      if (amountPaid > remainingAllowed) {
        toast.error(t('studentDetail.fees.appFeeExceedsRemaining', { max: remainingAllowed.toFixed(2) }));
        return;
      }
    }

    if (feeForm.fee_type === 'tuition_fee') {
      if (configuredTuitionFee <= 0) {
        toast.error(t('studentDetail.fees.tuitionFeeNotConfigured'));
        return;
      }
      const netTuition = Math.max(0, configuredTuitionFee - configuredScholarship);
      const alreadyPaid = getPaidByType('tuition_fee', editingFeeId);
      const remainingAllowed = Math.max(0, netTuition - alreadyPaid);
      if (amountPaid > remainingAllowed) {
        toast.error(t('studentDetail.fees.tuitionFeeExceedsRemaining', { max: remainingAllowed.toFixed(2) }));
        return;
      }
    }

    try {
      setFeeSaving(true);
      const feeData = new FormData();
      feeData.append('fee_type', feeForm.fee_type);
      feeData.append('description', feeForm.description || '');
      feeData.append('currency', feeForm.currency);
      feeData.append('amount_paid', String(amountPaid));
      feeData.append('payment_date', feeForm.payment_date);
      feeData.append('payment_method', feeForm.payment_method || '');
      feeData.append('reference_no', feeForm.reference_no || '');
      feeData.append('notes', feeForm.notes || '');

      if (feeForm.payment_receipt_copy instanceof File) {
        feeData.append('payment_receipt_copy_file', feeForm.payment_receipt_copy);
      } else if (editingFee && editingFee.payment_receipt_copy) {
        feeData.append('payment_receipt_copy', editingFee.payment_receipt_copy);
      }

      if (editingFee) {
        await feesAPI.update(editingFee.fee_id, feeData);
        toast.success(t('studentDetail.fees.feeUpdated'));
      } else {
        await feesAPI.create(id, feeData);
        toast.success(t('studentDetail.fees.feeCreated'));
      }

      setShowFeeModal(false);
      setEditingFee(null);
      setFeeForm({
        fee_type: defaultFeeType,
        description: '',
        currency: 'EURO',
        amount_paid: '',
        payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
        payment_method: '',
        reference_no: '',
        payment_receipt_copy: null,
        payment_receipt_copy_name: '',
        notes: ''
      });
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.fees.feeSaveFailed'));
    } finally {
      setFeeSaving(false);
    }
  };

  const handleSaveRefund = async () => {
    if (!refundForm.refund_type || !refundForm.currency || refundForm.amount_refunded === '') {
      toast.error(t('studentDetail.refunds.fillRequired'));
      return;
    }

    const amountRefunded = parseFloat(refundForm.amount_refunded);
    if (isNaN(amountRefunded) || amountRefunded <= 0) {
      toast.error(t('studentDetail.refunds.invalidAmount'));
      return;
    }

    try {
      setRefundSaving(true);
      const payload = {
        refund_type: refundForm.refund_type,
        description: refundForm.description || '',
        currency: refundForm.currency,
        amount_refunded: String(amountRefunded),
        notes: refundForm.notes || ''
      };

      if (editingRefund) {
        await refundsAPI.update(editingRefund.refund_id, payload);
        toast.success(t('studentDetail.refunds.refundUpdated'));
      } else {
        await refundsAPI.create(id, payload);
        toast.success('Refund request submitted for approval');
      }

      setShowRefundModal(false);
      setEditingRefund(null);
      setRefundForm({
        refund_type: 'application_fee',
        description: '',
        currency: 'EURO',
        amount_refunded: '',
        refund_method: '',
        reference_no: '',
        refund_receipt_copy: null,
        refund_receipt_copy_name: '',
        notes: ''
      });
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.refunds.refundSaveFailed'));
    } finally {
      setRefundSaving(false);
    }
  };

  const handleUpdateApp = async () => {
    try {
      const scholarshipValue = appForm.scholarship === '' || appForm.scholarship === null || appForm.scholarship === undefined
        ? null
        : Number(appForm.scholarship);
      if (scholarshipValue !== null && (Number.isNaN(scholarshipValue) || scholarshipValue < 0 || scholarshipValue > 3000)) {
        toast.error(t('studentDetail.toasts.scholarshipRange'));
        return;
      }

      const isAppFeeApplicable = Boolean(appForm.application_fee_applicable);
      const appFeeValue = appForm.application_fee === '' || appForm.application_fee === null || appForm.application_fee === undefined
        ? null
        : Number(appForm.application_fee);
      if (isAppFeeApplicable) {
        if (appFeeValue === null || Number.isNaN(appFeeValue) || appFeeValue < 0 || appFeeValue > 300) {
          toast.error(t('studentDetail.toasts.appFeeRange'));
          return;
        }
      }

      const payload = {
        course_id: appForm.course_id ?? null,
        intake_id: appForm.intake_id ?? null,
        tuition_fee: appForm.tuition_fee === '' || appForm.tuition_fee === null || appForm.tuition_fee === undefined
          ? null
          : Number(appForm.tuition_fee),
        fee_currency: appForm.fee_currency || null,
        scholarship: scholarshipValue,
        application_fee: isAppFeeApplicable ? appFeeValue : null,
        app_date: appForm.app_date || (appForm.created_at ? String(appForm.created_at).slice(0, 10) : null),
        notes: appForm.notes ?? null
      };

      await applicationsAPI.update(appForm.app_id, payload);
      toast.success(t('studentDetail.toasts.applicationUpdated'));
      loadStudent();
    } catch (err) { toast.error(err.response?.data?.message || t('studentDetail.toasts.applicationUpdateFailed')); }
  };

  const handleSaveVisaDetails = async () => {
    try {
      setVisaSaving(true);
      await studentsAPI.updateVisa(id, {
        visa_status: visaForm.visa_status || null,
        visa_result: visaForm.visa_result || null
      });
      toast.success(t('studentDetail.visa.updated'));
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.visa.updateFailed'));
    } finally {
      setVisaSaving(false);
    }
  };

  const handleFeeVerification = async (feeId, decision) => {
    try {
      await feesAPI.verify(feeId, decision);
      toast.success(decision === 'verified' ? t('studentDetail.fees.feeVerified') : t('studentDetail.fees.feeMarkedNotVerified'));
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.fees.feeVerifyFailed'));
    }
  };

  const handleSaveEnrollmentNumber = async () => {
    if (!['super_admin', 'admin'].includes(String(currentUser?.role || '').toLowerCase())) return;
    setEnrollmentSaving(true);
    try {
      await studentsAPI.update(id, { enrollment_number: enrollmentInput.trim() || '' });
      toast.success(t('studentDetail.fees.enrollmentSaved'));
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.fees.enrollmentFailed'));
    } finally {
      setEnrollmentSaving(false);
    }
  };

  const handleInterviewOutcomeUpdate = async (interviewId, outcome) => {
    try {
      await interviewsAPI.update(interviewId, { outcome });
      toast.success(
        t('studentDetail.interviews.markedAs', {
          outcome: String(outcome).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        })
      );
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.interviews.updateFailed'));
    }
  };

  const openInterviewOutcomeOrFranceModal = (interview, outcome) => {
    if (isFranceCampusName(student?.campus_name)) {
      setFranceInterviewOutcomeDialog({ interview, outcome });
      return;
    }
    handleInterviewOutcomeUpdate(interview.interview_id, outcome);
  };

  const handleMockInterviewOutcomeUpdate = async (interviewId, outcome) => {
    try {
      await mockInterviewsAPI.update(interviewId, { outcome });
      toast.success(
        t('studentDetail.mockInterviews.markedAs', {
          outcome: String(outcome).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        })
      );
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.mockInterviews.updateFailed'));
    }
  };

  const openInterviewReport = async (interviewId) => {
    try {
      const { data } = await interviewsAPI.getReportUrl(interviewId);
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.toasts.couldNotOpenReport'));
    }
  };

  const syncInterviewZoom = async (interviewId) => {
    setZoomInterviewSyncingId(interviewId);
    try {
      const res = await zoomAPI.sync({ entity_type: 'interview', entity_id: interviewId });
      toast.success(t('studentDetail.toasts.zoomSynced'));
      if (res.data?.errors?.length) {
        toast.warn(Array.isArray(res.data.errors) ? res.data.errors.join(' ') : String(res.data.errors));
      }
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.toasts.syncFailed'));
    } finally {
      setZoomInterviewSyncingId(null);
    }
  };

  const syncMockInterviewZoom = async (mockInterviewId) => {
    setZoomMockSyncingId(mockInterviewId);
    try {
      const res = await zoomAPI.sync({ entity_type: 'mock_interview', entity_id: mockInterviewId });
      toast.success(t('studentDetail.toasts.zoomSynced'));
      if (res.data?.errors?.length) {
        toast.warn(Array.isArray(res.data.errors) ? res.data.errors.join(' ') : String(res.data.errors));
      }
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.toasts.syncFailed'));
    } finally {
      setZoomMockSyncingId(null);
    }
  };

  const handleOpenRescheduleModal = (interview) => {
    if (interview.outcome && interview.outcome !== 'pending') {
      toast.error(t('studentDetail.toasts.cannotRescheduleAfterOutcome'));
      return;
    }
    setReschedulingInterview(interview);
    const tz = interview.timezone || resolveDefaultInterviewTimezone(interviewTimezones);
    const scheduledDateTime = interview.scheduled_at ? storedDatetimeToLocalInput(interview.scheduled_at) : '';
    const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : getTodayDateInputMin();
    const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
    setRescheduleSlotDate(initDate);
    setDesiredRescheduleSlotTime(initTime);
    setRescheduleSlotId('');
    setRescheduleForm({
      scheduled_at: scheduledDateTime,
      timezone: tz,
      interview_link: interview.interview_link || ''
    });
    setShowRescheduleModal(true);
  };

  const handleRescheduleInterview = async () => {
    if (!reschedulingInterview) return;
    if (!rescheduleForm.scheduled_at) {
      toast.error(t('studentDetail.toasts.selectRescheduleDate'));
      return;
    }
    if (!rescheduleForm.timezone) {
      toast.error(t('studentDetail.toasts.selectTimezone'));
      return;
    }
    if (!isDateTimeLocalInFuture(rescheduleForm.scheduled_at)) {
      toast.error(t('studentDetail.toasts.futureDateRequired'));
      return;
    }

    setRescheduleLoading(true);
    try {
      // Reschedule-only payload: the backend recognises this shape and
      // permits any authenticated user (see `allowRescheduleOrUpdatePermission`
      // in routes/interviews.js).  We deliberately omit `outcome` — the
      // reschedule button only shows when outcome is already 'pending'.
      await interviewsAPI.update(reschedulingInterview.interview_id, {
        scheduled_at: rescheduleForm.scheduled_at,
        interview_link: rescheduleForm.interview_link || null,
        timezone: rescheduleForm.timezone,
        status: 'rescheduled'
      });
      toast.success(t('studentDetail.toasts.interviewRescheduled'));
      setShowRescheduleModal(false);
      setReschedulingInterview(null);
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.toasts.interviewRescheduleFailed'));
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleOpenMockRescheduleModal = (interview) => {
    if (interview.outcome && interview.outcome !== 'pending') {
      toast.error(t('studentDetail.toasts.cannotRescheduleAfterOutcome'));
      return;
    }
    setReschedulingMockInterview(interview);
    const tz = interview.timezone || resolveDefaultInterviewTimezone(interviewTimezones);
    const scheduledDateTime = interview.scheduled_at ? storedDatetimeToLocalInput(interview.scheduled_at) : '';
    const initDate = scheduledDateTime ? scheduledDateTime.slice(0, 10) : getTodayDateInputMin();
    const initTime = scheduledDateTime ? scheduledDateTime.slice(11, 16) : '';
    setMockRescheduleSlotDate(initDate);
    setDesiredMockRescheduleSlotTime(initTime);
    setMockRescheduleSlotId('');
    setRescheduleForm({
      scheduled_at: scheduledDateTime,
      timezone: tz,
      interview_link: interview.interview_link || ''
    });
    setShowMockRescheduleModal(true);
  };

  useEffect(() => {
    if (!showRescheduleModal || !rescheduleSlotDate) {
      if (!showRescheduleModal) setRescheduleSlotOptions([]);
      return;
    }
    setRescheduleSlotsLoading(true);
    settingsAPI
      .getInterviewSlotAvailability({ date: rescheduleSlotDate, type: 'interview' })
      .then((res) => setRescheduleSlotOptions(res.data || []))
      .catch(() => setRescheduleSlotOptions([]))
      .finally(() => setRescheduleSlotsLoading(false));
  }, [showRescheduleModal, rescheduleSlotDate]);

  useEffect(() => {
    if (!showMockRescheduleModal || !mockRescheduleSlotDate) {
      if (!showMockRescheduleModal) setMockRescheduleSlotOptions([]);
      return;
    }
    setMockRescheduleSlotsLoading(true);
    settingsAPI
      .getInterviewSlotAvailability({ date: mockRescheduleSlotDate, type: 'mock' })
      .then((res) => setMockRescheduleSlotOptions(res.data || []))
      .catch(() => setMockRescheduleSlotOptions([]))
      .finally(() => setMockRescheduleSlotsLoading(false));
  }, [showMockRescheduleModal, mockRescheduleSlotDate]);

  useEffect(() => {
    if (!showRescheduleModal) return;
    const selected = (rescheduleSlotOptions || []).find((s) => String(s.slot_id) === String(rescheduleSlotId));
    const start = selected?.start_time ? String(selected.start_time).slice(0, 5) : '';
    setRescheduleForm((f) => ({ ...f, scheduled_at: scheduledAtFromSlot(rescheduleSlotDate, start) }));
  }, [showRescheduleModal, rescheduleSlotDate, rescheduleSlotId, rescheduleSlotOptions]);

  useEffect(() => {
    if (!showRescheduleModal) return;
    if (rescheduleSlotId) return;
    if (!desiredRescheduleSlotTime) return;
    const match = (rescheduleSlotOptions || []).find((s) => String(s.start_time || '').slice(0, 5) === String(desiredRescheduleSlotTime).slice(0, 5));
    if (match?.slot_id != null) setRescheduleSlotId(String(match.slot_id));
  }, [showRescheduleModal, rescheduleSlotOptions, desiredRescheduleSlotTime, rescheduleSlotId]);

  useEffect(() => {
    if (!showMockRescheduleModal) return;
    const selected = (mockRescheduleSlotOptions || []).find((s) => String(s.slot_id) === String(mockRescheduleSlotId));
    const start = selected?.start_time ? String(selected.start_time).slice(0, 5) : '';
    setRescheduleForm((f) => ({ ...f, scheduled_at: scheduledAtFromSlot(mockRescheduleSlotDate, start) }));
  }, [showMockRescheduleModal, mockRescheduleSlotDate, mockRescheduleSlotId, mockRescheduleSlotOptions]);

  useEffect(() => {
    if (!showMockRescheduleModal) return;
    if (mockRescheduleSlotId) return;
    if (!desiredMockRescheduleSlotTime) return;
    const match = (mockRescheduleSlotOptions || []).find((s) => String(s.start_time || '').slice(0, 5) === String(desiredMockRescheduleSlotTime).slice(0, 5));
    if (match?.slot_id != null) setMockRescheduleSlotId(String(match.slot_id));
  }, [showMockRescheduleModal, mockRescheduleSlotOptions, desiredMockRescheduleSlotTime, mockRescheduleSlotId]);

  const handleRescheduleMockInterview = async () => {
    if (!reschedulingMockInterview) return;
    if (!rescheduleForm.scheduled_at) {
      toast.error(t('studentDetail.toasts.selectRescheduleDate'));
      return;
    }
    if (!rescheduleForm.timezone) {
      toast.error(t('studentDetail.toasts.selectTimezone'));
      return;
    }
    if (!isDateTimeLocalAtLeastHoursInFuture(rescheduleForm.scheduled_at, 3)) {
      toast.error(t('studentDetail.toasts.threeHoursFromNow'));
      return;
    }
    setMockRescheduleLoading(true);
    try {
      // Reschedule-only payload: the backend recognises this shape and
      // permits any authenticated user (see
      // `allowMockRescheduleOrUpdatePermission` in routes/mock-interviews.js).
      await mockInterviewsAPI.update(reschedulingMockInterview.mock_interview_id, {
        scheduled_at: rescheduleForm.scheduled_at,
        interview_link: rescheduleForm.interview_link || null,
        timezone: rescheduleForm.timezone,
        status: 'rescheduled'
      });
      toast.success(t('studentDetail.toasts.mockRescheduled'));
      setShowMockRescheduleModal(false);
      setReschedulingMockInterview(null);
      loadStudent();
    } catch (err) {
      toast.error(err.response?.data?.message || t('studentDetail.toasts.mockRescheduleFailed'));
    } finally {
      setMockRescheduleLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    settingsAPI
      .getLetterGenerationAccess()
      .then((res) => {
        if (!cancelled) setLetterAccessConfig(res.data);
      })
      .catch(() => {
        if (!cancelled) setLetterAccessConfig({ access_mode: 'all', specific_user_ids: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ----- Sticky tabs via URL (?tab=documents) -----
  // These hooks must be declared before any conditional return.
  const canViewEmailsTab = true;
  /** Student Emails tab: France CRM → any role may compose; India (and other non-France) CRM → super_admin / admin only. */
  const studentEmailComposeFlags = useMemo(() => {
    const crm = String(activeCountry || '').toUpperCase();
    const role = String(currentUser?.role || '').toLowerCase();
    const isAdminOrSuperAdmin = ['super_admin', 'admin'].includes(role);
    if (crm === 'FRANCE') {
      return { allowSending: true, useParentSendPermissionOnly: true };
    }
    return {
      allowSending: isAdminOrSuperAdmin,
      useParentSendPermissionOnly: false
    };
  }, [activeCountry, currentUser?.role]);
  const canUseStudentLettersTab = useMemo(() => {
    if (letterAccessConfig == null || !currentUser) return false;
    return canAccessStudentLetterGeneration(currentUser, letterAccessConfig);
  }, [letterAccessConfig, currentUser]);

  const showMeetingsTab = countryConfig.features.meetingRequired !== false;
  const showMockInterviewsTab = countryConfig.features.mockInterviewRequired !== false || !!student?.mock_interview_enabled;
  const showJobsTab = String(student?.country_code || '').toUpperCase() === 'FRANCE';
  const isStudentEU = isEUNationality(student?.country);
  const showVisaTabs = countryConfig.features.visaModule === true ||
    (countryConfig.features.visaModule === 'conditional' && !isStudentEU);
  const showRefundsTab = countryConfig.features.refundModule === true ||
    (countryConfig.features.refundModule === 'conditional' && !isStudentEU);

  const visibleTabs = useMemo(() => {
    return [
      'personal',
      'application',
      'documents',
      ...(showMockInterviewsTab && student?.mock_interview_enabled ? ['mock-interviews'] : []),
      'interviews',
      'fees',
      ...(canUseStudentLettersTab ? ['letters'] : []),
      ...(showVisaTabs ? ['visa'] : []),
      ...(showVisaTabs ? ['visa-documents'] : []),
      ...(showRefundsTab ? ['refunds'] : []),
      ...(canViewEmailsTab ? ['emails'] : []),
      ...(showMeetingsTab ? ['meetings'] : []),
      ...(showJobsTab ? ['jobs'] : []),
      'activities',
      'audit'
    ];
  }, [student?.mock_interview_enabled, student?.country_code, canViewEmailsTab, canUseStudentLettersTab, showMeetingsTab, showMockInterviewsTab, showVisaTabs, showRefundsTab, showJobsTab]);

  const setTab = (tab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab) next.set('tab', tab);
      else next.delete('tab');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (letterAccessConfig == null) return;
    if (activeTab === 'letters' && !canUseStudentLettersTab) {
      setActiveTab('personal');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'personal');
        return next;
      }, { replace: true });
    }
  }, [letterAccessConfig, activeTab, canUseStudentLettersTab, setSearchParams]);

  useEffect(() => {
    if (!student) return;
    const requested = searchParams.get('tab');
    if (!requested) return;
    if (!visibleTabs.includes(requested)) return;
    if (activeTab === requested) return;
    setActiveTab(requested);
  }, [student, searchParams, visibleTabs, activeTab]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;
  if (!student) return <div className="empty-state"><p>{t('studentDetail.notFound')}</p></div>;

  const normalMandatoryDocs = student.mandatory_docs;
  const visaMandatoryDocs = student.mandatory_visa_docs || [];
  const normalDocuments = (student.documents || []).filter(d => (d.document_category || 'documents') === 'documents');
  const visaDocuments = (student.documents || []).filter(d => (d.document_category || 'documents') === 'visa_documents');
  const uploadedNormalTypes = normalDocuments.map(d => d.document_type);
  const uploadedVisaTypes = visaDocuments.map(d => d.document_type);
  const isB2BStudent = Boolean(student.agency_id);
  const primaryApplication = student.applications?.[0] || null;
  const studentIntakeLine = [student.intake_name, student.student_intake_year]
    .filter((x) => x != null && String(x).trim() !== '')
    .join(' ');
  const appIntakeLine = primaryApplication
    ? [primaryApplication.application_intake_name, primaryApplication.application_intake_year]
        .filter((x) => x != null && String(x).trim() !== '')
        .join(' ')
    : '';
  const intakePersonalDisplay = studentIntakeLine || appIntakeLine || '-';
  const tuitionPersonalDisplay =
    primaryApplication != null &&
    primaryApplication.tuition_fee != null &&
    String(primaryApplication.tuition_fee).trim() !== ''
      ? `${primaryApplication.tuition_fee}${
          primaryApplication.fee_currency ? ` ${primaryApplication.fee_currency}` : ''
        }`.trim()
      : '-';
  const programTypePersonalDisplay = formatProgramTypeForDetail(
    primaryApplication?.program_type || primaryApplication?.course_program_type
  );

  const hasUploadedRequiredDocuments = (normalMandatoryDocs || []).every((docType) =>
    uploadedNormalTypes.includes(docType)
  );
  const totalInterviews = (interviews || []).length;
  const hasPassedInterview = (interviews || []).some((interview) => interview.outcome === 'pass');
  const hasReachedInterviewLimit = totalInterviews >= 3;
  const hasPendingOutcomeInterview = (interviews || []).some(
    (interview) => !interview.outcome || interview.outcome === 'pending'
  );
  const totalMockInterviews = (mockInterviews || []).length;
  const hasPassedMockInterview = (mockInterviews || []).some((interview) => interview.outcome === 'pass');
  const hasReachedMockInterviewLimit = totalMockInterviews >= 3;
  const hasPendingOutcomeMockInterview = (mockInterviews || []).some(
    (interview) => !interview.outcome || interview.outcome === 'pending'
  );
  const mockGateRequired = countryConfig.features.mockInterviewRequired !== false;
  const canScheduleInterview =
    hasUploadedRequiredDocuments &&
    (!mockGateRequired || !student.mock_interview_enabled || hasPassedMockInterview) &&
    !hasPassedInterview &&
    !hasReachedInterviewLimit &&
    !hasPendingOutcomeInterview;
  const canScheduleMockInterview =
    hasUploadedRequiredDocuments &&
    !hasPassedMockInterview &&
    !hasReachedMockInterviewLimit &&
    !hasPendingOutcomeMockInterview;
  const configuredApplicationFee = appForm.application_fee_applicable ? Number(appForm.application_fee || 0) : 0;
  const configuredTuitionFee = Number(appForm.tuition_fee || 0);
  const configuredScholarship = Number(appForm.scholarship || 0);
  const netTuitionPayable = Math.max(0, configuredTuitionFee - configuredScholarship);
  const isAdminUser = ['super_admin', 'admin'].includes(String(currentUser?.role || '').toLowerCase());
  const isSalesExecutiveUser = String(currentUser?.role || '').toLowerCase() === 'sales_executive';
  const isFranceStudent = String(student?.country_code || '').toUpperCase() === 'FRANCE';
  const canMarkInterviewOutcome = isAdminUser || (isFranceStudent && isSalesExecutiveUser);
  const paidApplicationTotal = (fees || [])
    .filter((f) => String(f.verification_status || '') === 'verified')
    .filter((f) => f.fee_type === 'application_fee')
    .reduce((sum, f) => sum + Number(f.amount_paid || 0), 0);
  const paidTuitionTotal = (fees || [])
    .filter((f) => String(f.verification_status || '') === 'verified')
    .filter((f) => f.fee_type === 'tuition_fee')
    .reduce((sum, f) => sum + Number(f.amount_paid || 0), 0);
  const refundedApplicationTotal = (refunds || [])
    .filter((r) => r.refund_type === 'application_fee')
    .reduce((sum, r) => sum + Number(r.amount_refunded || 0), 0);
  const refundedTuitionTotal = (refunds || [])
    .filter((r) => r.refund_type === 'tuition_fee')
    .reduce((sum, r) => sum + Number(r.amount_refunded || 0), 0);
  const applicationRemaining = Math.max(0, configuredApplicationFee - paidApplicationTotal);
  const tuitionRemaining = Math.max(0, netTuitionPayable - paidTuitionTotal);
  const hasVerifiedApplicationFee = (fees || []).some(
    (f) => f.fee_type === 'application_fee' && String(f.verification_status || '') === 'verified'
  );
  const hasVerifiedTuitionFee = (fees || []).some(
    (f) => f.fee_type === 'tuition_fee' && String(f.verification_status || '') === 'verified'
  );
  const uploadTypeOptions = (documentTypes || []).filter(d => d.category === (activeTab === 'visa-documents' ? 'visa_documents' : 'documents'));

  const openUploadModalForType = (documentType = '') => {
    const nextCategory = activeTab === 'visa-documents' ? 'visa_documents' : 'documents';
    const firstType = (documentTypes.find(d => d.category === nextCategory) || {}).document_type_name || '';
    const selectedType = documentType || firstType;
    setUploadForm({
      document_type: selectedType,
      custom_document_type: '',
      document_category: nextCategory,
      file: null,
      notes: ''
    });
    setShowUploadModal(true);
  };
  const getPaidByType = (feeType, excludeFeeId = null) => {
    return (fees || [])
      .filter((f) => String(f.verification_status || '') === 'verified')
      .filter((f) => f.fee_type === feeType && (excludeFeeId === null || f.fee_id !== excludeFeeId))
      .reduce((sum, f) => sum + Number(f.amount_paid || 0), 0);
  };

  const isSuperAdminOnly = String(currentUser?.role || '').toLowerCase() === 'super_admin';

  const exportStudentCsv = () => {
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      const needs = /[",\n\r]/.test(s);
      const out = s.replace(/"/g, '""');
      return needs ? `"${out}"` : out;
    };
    const add = (row) => rows.push(row.map(esc).join(','));
    const rows = [];

    const now = new Date();
    const fileNameSafe = String(student?.student_ref || student?.student_id || 'student')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 80);
    const fileName = `${fileNameSafe}_export_${now.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;

    // --- Student summary
    add(['SECTION', 'Student']);
    add(['field', 'value']);
    const app0 = student?.applications?.[0];
    const studentFields = [
      ['student_id', student?.student_id],
      ['student_ref', student?.student_ref],
      ['student_type', student?.student_type],
      ['name', `${student?.title || ''} ${student?.first_name || ''} ${student?.last_name || ''}`.trim()],
      ['dob', student?.dob],
      ['gender', student?.gender],
      ['passport_no', student?.passport_no],
      ['address', student?.address],
      ['city', student?.city],
      ['state', student?.state],
      ['country', student?.country],
      ['zip_code', student?.zip_code],
      ['email', student?.email],
      ['phone', `${student?.phone_country_code || ''} ${student?.phone || ''}`.trim()],
      ['current_degree', student?.current_degree],
      ['source', student?.source],
      ['status', student?.status],
      ['campus', student?.campus_name],
      ['course', app0?.course_name],
      ['program_type', app0?.program_type || app0?.course_program_type],
      ['tuition_fee', app0?.tuition_fee],
      ['fee_currency', app0?.fee_currency],
      ['intake_name', student?.intake_name],
      ['intake_year', student?.student_intake_year],
      ['application_intake', app0?.application_intake_name],
      ['application_intake_year', app0?.application_intake_year],
      ['owner', student?.owner_name],
      ['agency', student?.agency_name],
      ['agency_email', student?.agency_email],
      ['lead_ref', student?.lead_ref],
      ['original_lead_id', student?.original_lead_id],
      ['is_direct_entry', student?.is_direct_entry],
      ['created_at', student?.created_at],
      ['updated_at', student?.updated_at]
    ];
    studentFields.forEach(([k, v]) => add([k, v]));
    add(['', '']);

    // --- Application (single row if present)
    add(['SECTION', 'Application']);
    add(['field', 'value']);
    Object.entries(appForm || {}).forEach(([k, v]) => add([k, v]));
    add(['', '']);

    // --- Visa
    add(['SECTION', 'Visa']);
    add(['field', 'value']);
    add(['visa_status', visaForm?.visa_status]);
    add(['visa_result', visaForm?.visa_result]);
    add(['', '']);

    // --- Documents
    add(['SECTION', 'Documents']);
    add(['document_id', 'category', 'document_type', 'file_name', 'uploaded_by', 'created_at']);
    const docsAll = [...(student?.documents || [])];
    docsAll.forEach((d) =>
      add([
        d.document_id,
        d.document_category || 'documents',
        d.document_type,
        d.document_copy ? String(d.document_copy).split('/').pop() : (d.document_copy_name || ''),
        d.uploaded_by_name,
        d.created_at
      ])
    );
    add(['', '']);

    // --- Interviews
    add(['SECTION', 'Interviews']);
    add(['interview_id', 'scheduled_at', 'timezone', 'status', 'outcome', 'remarks', 'interview_comment', 'created_by']);
    (interviews || []).forEach((i) =>
      add([i.interview_id, i.scheduled_at, i.timezone, i.status, i.outcome, i.remarks, i.interview_comment, i.created_by_name])
    );
    add(['', '']);

    // --- Mock interviews
    add(['SECTION', 'Mock Interviews']);
    add(['mock_interview_id', 'scheduled_at', 'timezone', 'status', 'outcome', 'remarks', 'created_by']);
    (mockInterviews || []).forEach((i) =>
      add([i.mock_interview_id, i.scheduled_at, i.timezone, i.status, i.outcome, i.remarks, i.created_by_name])
    );
    add(['', '']);

    // --- Meetings
    add(['SECTION', 'Meetings']);
    add(['meeting_id', 'title', 'meeting_type', 'meeting_date', 'meeting_time', 'timezone', 'mode', 'status', 'outcome', 'created_by']);
    (meetings || []).forEach((m) =>
      add([m.meeting_id, m.title, m.meeting_type, m.meeting_date, m.meeting_time, m.timezone, m.mode, m.status, m.outcome, m.created_by_name])
    );
    add(['', '']);

    // --- Activities
    add(['SECTION', 'Activities']);
    add(['activity_id', 'activity_type', 'activity_date', 'activity_time', 'next_followup', 'notes', 'created_by']);
    (activities || []).forEach((a) =>
      add([a.activity_id, a.activity_type, a.activity_date, a.activity_time, a.next_followup, a.notes, a.created_by_name])
    );
    add(['', '']);

    // --- Fees
    add(['SECTION', 'Fees']);
    add(['fee_id', 'fee_type', 'currency', 'amount_paid', 'payment_date', 'verification_status', 'created_at']);
    (fees || []).forEach((f) =>
      add([f.fee_id, f.fee_type, f.currency, f.amount_paid, f.payment_date, f.verification_status, f.created_at])
    );
    add(['', '']);

    // --- Refunds
    add(['SECTION', 'Refunds']);
    add(['refund_id', 'refund_type', 'currency', 'amount_refunded', 'refund_date', 'refund_method', 'status', 'priority', 'created_at']);
    (refunds || []).forEach((r) =>
      add([r.refund_id, r.refund_type, r.currency, r.amount_refunded, r.refund_date, r.refund_method, r.status, r.priority, r.created_at])
    );
    add(['', '']);

    // --- Audit log
    add(['SECTION', 'Audit Log']);
    add(['id', 'entity_type', 'action', 'created_by', 'created_at', 'details']);
    (auditLog || []).forEach((l) =>
      add([l.id || l.audit_id, l.entity_type, l.action, l.created_by_name, l.created_at, l.details ? JSON.stringify(l.details) : ''])
    );

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div>
      {/* Header */}
      <div className="detail-header">
        <div className="detail-left">
          <h2>{student.title} {student.first_name} {student.last_name}</h2>
          <div className="detail-meta" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{student.student_ref}</span>
            {student.enrollment_number != null && String(student.enrollment_number).trim() !== '' && (
              <>
                <span style={{ color: 'var(--text-muted)' }} aria-hidden>|</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {t('studentDetail.enrollment')}{' '}
                  <strong style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {String(student.enrollment_number).trim()}
                  </strong>
                </span>
              </>
            )}
            <StatusBadge status={student.status} />
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
            {student.email} | {student.phone_country_code} {student.phone}
          </div>
        </div>
        <div className="detail-right">
          <div className="detail-owner">
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('studentDetail.ownerLabel')}</span>
            <strong>{student.owner_name || t('studentDetail.unassigned')}</strong>
          </div>
          {student.agency_name && <div style={{ fontSize: 13 }}>{t('studentDetail.agencyLabel')} <strong>{student.agency_name}</strong></div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canPerformAction('students', 'update') && <button className="btn btn-sm btn-outline" onClick={() => navigate(`/students/${id}/edit`)}><FiEdit /> {t('studentDetail.editBtn')}</button>}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="btn btn-sm btn-outline"
                  disabled={appFormLoading}
                  onClick={(e) => { e.stopPropagation(); setAppFormDropdownOpen(prev => !prev); }}
                  title={t('studentDetail.printApplicationForm')}
                >
                  <FiFileText /> {appFormLoading ? t('common.generating') : t('studentDetail.applicationFormBtn')} ▾
                </button>
                {appFormDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute', top: '100%', left: 0, zIndex: 100,
                      background: 'var(--card-bg, #fff)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius, 6px)', boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                      minWidth: 200, marginTop: 4, overflow: 'hidden'
                    }}
                  >
                    <button
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '10px 16px', border: 'none', background: 'none',
                        cursor: 'pointer', fontSize: 13, color: 'var(--text)',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg, #f5f5f5)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      onClick={() => downloadApplicationForm('en')}
                    >
                      <FiFileText /> {t('studentDetail.appFormEnglish')}
                    </button>
                    <div style={{ height: 1, background: 'var(--border)' }} />
                    <button
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '10px 16px', border: 'none', background: 'none',
                        cursor: 'pointer', fontSize: 13, color: 'var(--text)',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg, #f5f5f5)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      onClick={() => downloadApplicationForm('fr')}
                    >
                      <FiFileText /> {t('studentDetail.appFormFrench')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                className="btn btn-sm btn-outline"
                disabled={appFormLoading}
                onClick={() => downloadApplicationForm()}
                title={t('studentDetail.printApplicationForm')}
              >
                <FiFileText /> {appFormLoading ? t('common.generating') : t('studentDetail.applicationFormBtn')}
              </button>
            )}
            {isSuperAdminOnly && (
              <button className="btn btn-sm btn-outline" onClick={exportStudentCsv} title={t('studentDetail.exportCsvTitle')}>
                <FiDownload /> {t('common.exportCsv')}
              </button>
            )}
            {student.lead_ref && (
              <button className="btn btn-sm btn-outline" onClick={() => navigate(`/b2b-agencies/${student.original_lead_id}`)}>{t('studentDetail.viewAgency')}</button>
            )}
          </div>
          {/* Document completion warning */}
          {!student.docs_complete && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: 'var(--warning-light)',
              borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--orange)',
              border: '1px solid var(--warning)', fontWeight: 500
            }}>
              &#9888; {t('studentDetail.documents.missingMandatory', { count: student.missing_docs?.length })}
            </div>
          )}
          {activeTab === 'visa-documents' && showVisaTabs && student.missing_visa_docs?.length > 0 && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: 'var(--warning-light)',
              borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--orange)',
              border: '1px solid var(--warning)', fontWeight: 500
            }}>
              &#9888; {t('studentDetail.documents.missingMandatoryVisa', { count: student.missing_visa_docs?.length })}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {visibleTabs.map(tab => {
          const tabLabelMap = {
            personal: t('studentDetail.tabs.personal'),
            application: t('studentDetail.tabs.application'),
            documents: t('studentDetail.tabs.documents'),
            interviews: t('studentDetail.tabs.interviews'),
            'mock-interviews': t('studentDetail.tabs.mockInterviews'),
            fees: t('studentDetail.tabs.fees'),
            refunds: t('studentDetail.tabs.refunds'),
            emails: t('studentDetail.tabs.emails'),
            meetings: t('studentDetail.tabs.meetings'),
            jobs: t('jobs.title', 'Jobs'),
            activities: t('studentDetail.tabs.activities'),
            audit: t('studentDetail.tabs.audit'),
            'lead-history': t('studentDetail.tabs.leadHistory'),
            visa: t('studentDetail.tabs.visa'),
            'visa-documents': t('studentDetail.tabs.visaDocs'),
            letters: t('studentDetail.tabs.letters')
          };
          return (
            <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setTab(tab)}>
              {tabLabelMap[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Personal Info */}
      {activeTab === 'personal' && (
        <div className="card">
          <div className="detail-info-grid">
            <div className="detail-info-item"><label>{t('studentDetail.personal.studentId')}</label><span>{student.student_ref}</span></div>
            <div className="detail-info-item">
              <label>{t('studentDetail.personal.enrollmentNo')}</label>
              <span style={{ fontWeight: student.enrollment_number ? 600 : 400 }}>
                {student.enrollment_number != null && String(student.enrollment_number).trim() !== ''
                  ? String(student.enrollment_number).trim()
                  : '—'}
              </span>
            </div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.studentType')}</label><span>{String(student.student_type || '').toLowerCase() === 'b2b' ? t('studentDetail.personal.b2bAgencyReferred') : t('studentDetail.personal.b2cDirect')}</span></div>
            <div className="detail-info-item">
              <label>{isFranceCRM ? t('students.tableAdmissionStatus') : t('studentDetail.personal.status')}</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <StatusBadge status={student.status} />
                {canPerformAction('students', 'update') && Number(student.status_manual_override) === 1 && (
                  <button type="button" className="btn btn-sm btn-outline" onClick={handleResumeAutoStatus}>
                    {t('studentDetail.resumeAutoStatus')}
                  </button>
                )}
              </span>
            </div>
            {canViewCompanyStatus && (
              <div className="detail-info-item">
                <label>{t('studentDetail.personal.companyStatus')}</label>
                {canEditCompanyStatus ? (
                  <div style={{ display: 'grid', gap: 6, maxWidth: 280 }}>
                    <select
                      value={student.company_status || ''}
                      disabled={updatingCompanyStatus}
                      onChange={(e) => handleCompanyStatusUpdate(e.target.value)}
                      style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '8px 10px', background: 'var(--card-bg)', fontSize: 13, fontWeight: 600 }}
                    >
                      <option value="">{t('students.selectCompanyStatus')}</option>
                      {companyStatusOptions.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                    {updatingCompanyStatus && (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('students.updating')}</span>
                    )}
                  </div>
                ) : (
                  <span>{student.company_status ? <StatusBadge status={student.company_status} /> : '-'}</span>
                )}
              </div>
            )}
            <div className="detail-info-item"><label>{t('studentDetail.personal.directEntry')}</label><span>{Number(student.is_direct_entry) === 1 ? t('common.yes') : t('common.no')}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.title')}</label><span>{student.title || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.firstName')}</label><span>{student.first_name}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.lastName')}</label><span>{student.last_name}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.dob')}</label><span>{student.dob ? new Date(student.dob).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span></div>
            <div className="detail-info-item" style={{ gridColumn: '1 / -1' }}><label>{t('studentDetail.personal.address')}</label><span>{student.address || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.country')}</label><span>{student.country || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.state')}</label><span>{student.state || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.city')}</label><span>{student.city || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.zipCode')}</label><span>{student.zip_code || '-'}</span></div>
            <div className="detail-info-item">
              <label>{t('studentDetail.personal.phone')}</label>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {student.phone ? (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(`${student.phone_country_code || ''} ${student.phone || ''}`.trim(), 'phone')}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    title="Copy phone number"
                  >
                    <span>{`${student.phone_country_code || ''} ${student.phone || ''}`.trim()}</span>
                    <FiCopy size={13} />
                  </button>
                ) : '-'}
                {canPerformAction('students', 'read') && student.phone && (
                  <Link
                    to={`/students?phone=${encodeURIComponent(String(student.phone).replace(/\D/g, ''))}`}
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {t('studentDetail.personal.searchByPhone')}
                  </Link>
                )}
              </span>
            </div>
            <div className="detail-info-item">
              <label>{t('studentDetail.personal.email')}</label>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {student.email ? (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(student.email, 'email')}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    title="Copy email"
                  >
                    <span>{student.email}</span>
                    <FiCopy size={13} />
                  </button>
                ) : '-'}
                {canPerformAction('students', 'read') && student.email && (
                  <Link
                    to={`/students?email=${encodeURIComponent(String(student.email).trim())}`}
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {t('studentDetail.personal.searchByEmail')}
                  </Link>
                )}
              </span>
            </div>
            <div className="detail-info-item">
              <label>{t(isFranceCRM ? 'studentDetail.personal.passportNumberFrance' : 'studentDetail.personal.passportNumber')}</label>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {student.passport_no || '-'}
                {canPerformAction('students', 'read') && student.passport_no && (
                  <Link
                    to={`/students?passport_no=${encodeURIComponent(String(student.passport_no).trim())}`}
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {t('studentDetail.personal.searchByPassport')}
                  </Link>
                )}
              </span>
            </div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.gender')}</label><span>{student.gender || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.language')}</label><span>{student.language === 'FRENCH' ? t('studentForm.languageFrench') : t('studentForm.languageEnglish')}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.lastDegree')}</label><span>{student.current_degree || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.campus')}</label><span>{student.campus_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.course')}</label><span>{primaryApplication?.course_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.programType')}</label><span>{programTypePersonalDisplay}</span></div>
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item">
                <label>{t('studentDetail.personal.formationType')}</label>
                <span>
                  {student.formation_type === 'alternance'
                    ? t('studentForm.formationAlternance')
                    : student.formation_type === 'initial'
                      ? t('studentForm.formationInitial')
                      : '-'}
                </span>
              </div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.internshipProgram')}</label><span>{student.internship_program || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.statusDesired')}</label><span>{student.status_desired || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.businessSpecialization')}</label><span>{student.business_specialization || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.socialSecurityNumber')}</label><span>{student.social_security_number || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.isDisabled')}</label><span>{student.is_disabled || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.howFoundSchool')}</label><span>{student.how_found_school || '-'}</span></div>
            )}
            {String(student.country_code || '').toUpperCase() === 'FRANCE' && (
              <div className="detail-info-item"><label>{t('studentForm.itKnowledge')}</label><span>{student.it_knowledge || '-'}</span></div>
            )}
            <div className="detail-info-item"><label>{t('studentDetail.personal.tuitionFee')}</label><span>{tuitionPersonalDisplay}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.intake')}</label><span>{intakePersonalDisplay}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.source')}</label><span>{String(student.student_type || '').toLowerCase() === 'b2c' ? (student.source || '-') : '—'}</span></div>
            {isB2BStudent && <div className="detail-info-item"><label>{t('studentDetail.personal.tiedUpAgency')}</label><span>{student.agency_name || '-'}</span></div>}
            {student.lead_ref && (
              <div className="detail-info-item">
                <label>{t('studentDetail.personal.agencyRef')}</label>
                <span style={{ color: 'var(--primary)', cursor: 'pointer' }} onClick={() => navigate(`/b2b-agencies/${student.original_lead_id}`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(`/b2b-agencies/${student.original_lead_id}`)}>{student.lead_ref}</span>
              </div>
            )}
            <div className="detail-info-item"><label>{t('studentDetail.personal.owner')}</label><span>{student.owner_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.closedBy')}</label><span>{student.closed_by_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('studentDetail.personal.recordCreated')}</label><span>{student.created_at ? new Date(student.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span></div>
          </div>

          {/* France-only: Parent Information */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && (student.parent_address || student.father_phone || student.mother_phone || student.parent_city) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.parentInfo')}</h4>
              <div className="detail-info-grid">
                <div className="detail-info-item"><label>{t('studentForm.parentAddress')}</label><span>{student.parent_address || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.parentZipCode')}</label><span>{student.parent_zip_code || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.parentCity')}</label><span>{student.parent_city || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.parentState')}</label><span>{student.parent_state || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.parentCountry')}</label><span>{student.parent_country || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.fatherPhone')}</label><span>{student.father_phone || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.motherPhone')}</label><span>{student.mother_phone || '-'}</span></div>
              </div>
            </div>
          )}

          {/* France-only (by student country): Organization Follow-up */}
          {(
            String(student.location_country_code || '').toUpperCase() === 'FR'
            || String(student.country || '').trim().toLowerCase() === 'france'
          ) && (
            student.org_type || student.org_town || student.org_department ||
            student.org_advisor_name || student.org_advisor_email || student.org_advisor_phone ||
            student.org_registered_france_travail || student.org_registration_date ||
            student.org_identification_number || student.org_compensated_france_travail ||
            student.org_compensated_until_date
          ) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('studentForm.orgFollowUp.title')}</h4>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px', borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>
                {t('studentForm.orgFollowUp.subtitle')}
              </p>
              <div className="detail-info-grid">
                <div className="detail-info-item">
                  <label>{t('studentForm.orgFollowUp.type')}</label>
                  <span>
                    {student.org_type === 'Other'
                      ? (student.org_type_other ? `${t('studentForm.orgFollowUp.other')}: ${student.org_type_other}` : t('studentForm.orgFollowUp.other'))
                      : (student.org_type === 'Mission Locale' ? t('studentForm.orgFollowUp.missionLocale') : (student.org_type || '-'))}
                  </span>
                </div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.town')}</label><span>{student.org_town || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.department')}</label><span>{student.org_department || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.advisorName')}</label><span>{student.org_advisor_name || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.advisorEmail')}</label><span>{student.org_advisor_email || '-'}</span></div>
                <div className="detail-info-item">
                  <label>{t('studentForm.orgFollowUp.advisorPhone')}</label>
                  <span>{student.org_advisor_phone ? `${student.org_advisor_phone_country_code || ''} ${student.org_advisor_phone}`.trim() : '-'}</span>
                </div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.registered')}</label><span>{student.org_registered_france_travail || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.registrationDate')}</label><span>{student.org_registration_date ? String(student.org_registration_date).slice(0, 10) : '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.identificationNumber')}</label><span>{student.org_identification_number || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.compensated')}</label><span>{student.org_compensated_france_travail || '-'}</span></div>
                <div className="detail-info-item"><label>{t('studentForm.orgFollowUp.untilDate')}</label><span>{student.org_compensated_until_date ? String(student.org_compensated_until_date).slice(0, 10) : '-'}</span></div>
              </div>
            </div>
          )}

          {/* France-only: Studies Followed */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && Array.isArray(student.studies_followed) && student.studies_followed.some(r => r.year || r.establishment || r.degree_title) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.studiesFollowed')}</h4>
              <div className="table-responsive">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th style={{ width: 110 }}>{t('studentForm.sfLevel')}</th><th style={{ width: 80 }}>{t('studentForm.sfYear')}</th><th>{t('studentForm.sfEstablishment')}</th><th>{t('studentForm.sfDegreeTitle')}</th><th>{t('studentForm.sfComments')}</th></tr></thead>
                  <tbody>
                    {student.studies_followed.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 600 }}>{row.level || '-'}</td>
                        <td>{row.year || '-'}</td>
                        <td>{row.establishment || '-'}</td>
                        <td>{row.degree_title || '-'}</td>
                        <td>{row.comments || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* France-only: Modern Languages */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && Array.isArray(student.modern_languages) && student.modern_languages.some(r => r.language) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.modernLanguages')}</h4>
              <div className="table-responsive">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>{t('studentForm.mlLanguage')}</th><th>{t('studentForm.mlWritten')}</th><th>{t('studentForm.mlOral')}</th><th>{t('studentForm.mlDuration')}</th></tr></thead>
                  <tbody>
                    {student.modern_languages.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.language || '-'}</td>
                        <td>{row.written || '-'}</td>
                        <td>{row.oral || '-'}</td>
                        <td>{row.duration || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* France-only: Stays Abroad */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && Array.isArray(student.stays_abroad) && student.stays_abroad.some(r => r.country) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.staysAbroad')}</h4>
              <div className="table-responsive">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>{t('studentForm.saCountry')}</th><th>{t('studentForm.saDuration')}</th><th style={{ width: 80 }}>{t('studentForm.saYear')}</th><th>{t('studentForm.saActivities')}</th></tr></thead>
                  <tbody>
                    {student.stays_abroad.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.country || '-'}</td>
                        <td>{row.duration || '-'}</td>
                        <td>{row.year || '-'}</td>
                        <td>{row.activities || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* France-only: Work / Internships */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && Array.isArray(student.work_internships) && student.work_internships.some(r => r.company) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.workInternships')}</h4>
              <div className="table-responsive">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>{t('studentForm.wiCompany')}</th><th>{t('studentForm.wiDurationYear')}</th><th>{t('studentForm.wiMissions')}</th></tr></thead>
                  <tbody>
                    {student.work_internships.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.company || '-'}</td>
                        <td>{row.duration_year || '-'}</td>
                        <td>{row.missions || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* France-only: Cultural Activities */}
          {String(student.country_code || '').toUpperCase() === 'FRANCE' && Array.isArray(student.cultural_activities) && student.cultural_activities.some(r => r.activity) && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, borderBottom: '2px solid var(--border-color)', paddingBottom: 6 }}>{t('studentForm.culturalActivities')}</h4>
              <div className="table-responsive">
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>{t('studentForm.caActivity')}</th><th>{t('studentForm.caYears')}</th><th>{t('studentForm.caLevel')}</th></tr></thead>
                  <tbody>
                    {student.cultural_activities.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.activity || '-'}</td>
                        <td>{row.years || '-'}</td>
                        <td>{row.level || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <HistoryDetails
            title={t('studentDetail.historyTitles.personal')}
            logs={auditLog}
            filterLog={(l) => {
              const entity = String(l?.entity_type || '').toLowerCase();
              const action = String(l?.action || '').toLowerCase();
              // Keep this truly "personal": exclude visa + email audit entries (those belong in their own histories)
              if (action === 'visa_updated') return false;
              if (action.startsWith('email_')) return false;
              return entity === 'student';
            }}
          />
        </div>
      )}

      {/* Application */}
      {activeTab === 'application' && (
        <div className="card">
          <div style={{
            marginBottom: 16, padding: 12, borderRadius: 'var(--radius)', background: 'var(--bg-secondary)',
            border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)', display: 'grid', gap: 6
          }}>
            <div><strong>{t('studentDetail.application.studentType')}</strong> {isB2BStudent ? t('studentDetail.application.b2bStudent') : t('studentDetail.application.b2cStudent')}</div>
            {isB2BStudent && <div><strong>{t('studentDetail.application.agency')}</strong> {student.agency_name || '-'}</div>}
            <div><strong>{t('studentDetail.application.campus')}</strong> {student.campus_name || '-'}</div>
            <div><strong>{t('studentDetail.application.country')}</strong> {student.country || '-'}</div>
            <div><strong>{t(isFranceCRM ? 'studentDetail.application.passportNoFrance' : 'studentDetail.application.passportNo')}</strong> {student.passport_no || '-'}</div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <h3>{t('studentDetail.application.heading')}</h3>
          </div>

          {appForm.app_id ? (
            <div className="form-grid">
              <div className="form-group">
                <label>{t('studentDetail.application.tuitionFee')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ width: 100 }} value={appForm.fee_currency || 'GBP'} onChange={(e) => setAppForm(f => ({ ...f, fee_currency: e.target.value }))}>
                    <option value="EURO">{t('common.currencyEuro', 'EURO (Euro)')}</option>
                    <option value="AED">{t('common.currencyAed', 'AED (United Arab Emirates Dirham)')}</option>
                  </select>
                  <input
                    type="number"
                    value={appForm.tuition_fee || ''}
                    onChange={(e) => setAppForm(f => ({ ...f, tuition_fee: e.target.value }))}
                    placeholder={t('common.amount')}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('studentDetail.application.scholarship')}</label>
                <input
                  type="number"
                  min="0"
                  max="3000"
                  value={appForm.scholarship || ''}
                  onChange={(e) => setAppForm(f => ({ ...f, scholarship: e.target.value }))}
                  placeholder="0 - 3000"
                />
              </div>
              <div className="form-group">
                <label>{t('studentDetail.application.applicationDate')}</label>
                <input type="date" value={appForm.app_date ? appForm.app_date.slice(0, 10) : ''} readOnly />
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: 8 }}>{t('studentDetail.application.applicationFeeApplicable')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setAppForm((f) => {
                      const next = !Boolean(f.application_fee_applicable);
                      return {
                        ...f,
                        application_fee_applicable: next,
                        application_fee: next ? (f.application_fee || '') : ''
                      };
                    })}
                    aria-pressed={Boolean(appForm.application_fee_applicable)}
                    style={{
                      width: 46,
                      height: 26,
                      borderRadius: 999,
                      border: `1px solid ${appForm.application_fee_applicable ? 'var(--primary)' : 'var(--border)'}`,
                      background: appForm.application_fee_applicable ? 'var(--primary)' : 'var(--bg)',
                      cursor: 'pointer',
                      padding: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      transition: 'all 120ms ease'
                    }}
                    title={appForm.application_fee_applicable ? 'On' : 'Off'}
                  >
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#fff',
                        transform: appForm.application_fee_applicable ? 'translateX(20px)' : 'translateX(0px)',
                        transition: 'transform 120ms ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
                      }}
                    />
                  </button>

                  {appForm.application_fee_applicable && (
                    <input
                      type="number"
                      min="0"
                      max="300"
                      value={appForm.application_fee || ''}
                      onChange={(e) => setAppForm(f => ({ ...f, application_fee: e.target.value }))}
                      placeholder="0 - 300"
                      style={{ width: 140 }}
                    />
                  )}
                </div>
              </div>
              <div className="form-group full-width">
                <label>{t('studentDetail.application.notes')}</label>
                <textarea value={appForm.notes || ''} onChange={(e) => setAppForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="form-group full-width">
                <button className="btn btn-primary" onClick={handleUpdateApp}>{t('studentDetail.saveApplication')}</button>
              </div>
            </div>
          ) : (
            <div className="empty-state"><p>{t('studentDetail.application.noApplication')}</p></div>
          )}

          {/* Application History (compact) */}
          <HistoryDetails
            title={t('studentDetail.historyTitles.application')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'application'}
          />
        </div>
      )}

      {/* Visa */}
      {activeTab === 'visa' && showVisaTabs && (
        <div className="card">
          <div style={{ marginBottom: 16 }}>
            <h3>{t('studentDetail.visa.heading')}</h3>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('studentDetail.visa.visaStatus')}</label>
              <select
                value={visaForm.visa_status || ''}
                onChange={(e) => setVisaForm((prev) => ({ ...prev, visa_status: e.target.value }))}
              >
                <option value="">{t('studentDetail.visa.selectVisaStatus')}</option>
                {visaStatusOptions.map((status) => (
                  <option key={status.status_id} value={status.status_name}>{status.status_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('studentDetail.visa.visaResult')}</label>
              <select
                value={visaForm.visa_result || ''}
                onChange={(e) => setVisaForm((prev) => ({ ...prev, visa_result: e.target.value }))}
              >
                <option value="">{t('studentDetail.visa.selectVisaResult')}</option>
                {visaResultOptions.map((result) => (
                  <option key={result.result_id} value={result.result_name}>{result.result_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group full-width">
              <button className="btn btn-primary" onClick={handleSaveVisaDetails} disabled={visaSaving}>
                {visaSaving ? t('studentDetail.visa.savingBtn') : t('studentDetail.visa.saveBtn')}
              </button>
            </div>
          </div>

          <HistoryDetails
            title={t('studentDetail.historyTitles.visa')}
            logs={auditLog}
            filterLog={(l) => String(l?.action || '').toLowerCase() === 'visa_updated'}
          />
        </div>
      )}

      {/* Documents */}
      {(activeTab === 'documents' || (activeTab === 'visa-documents' && showVisaTabs && hasPassedInterview)) && (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <h3>{activeTab === 'visa-documents' ? t('studentDetail.documents.visaDocsHeading') : t('studentDetail.documents.heading')}</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openUploadModalForType()}
            >
              <FiUpload /> {t('studentDetail.uploadDocument')}
            </button>
          </div>

          {/* Mandatory docs checklist */}
          <div className="doc-checklist">
            {(activeTab === 'visa-documents' ? visaMandatoryDocs : normalMandatoryDocs).map(doc => {
              const isUploaded = (activeTab === 'visa-documents' ? uploadedVisaTypes : uploadedNormalTypes).includes(doc);
              return (
                <div key={doc} className={`doc-check-item ${isUploaded ? 'uploaded' : 'missing'}`}>
                  {isUploaded ? <FiCheck color="var(--success)" /> : <FiX color="var(--danger)" />}
                  {!isUploaded ? (
                    <button
                      type="button"
                      onClick={() => openUploadModalForType(doc)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--primary)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit',
                        textAlign: 'left'
                      }}
                    >
                      {doc}
                    </button>
                  ) : (
                    <span>{doc}</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11 }}>
                    {isUploaded ? t('studentDetail.documents.uploaded') : t('studentDetail.documents.requiredClickUpload')}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Document list */}
          <div className="table-container">
            <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>{t('studentDetail.documents.docType')}</th>
                  <th>{t('studentDetail.documents.fileName')}</th>
                  <th>{t('studentDetail.documents.uploadedDate')}</th>
                  <th>{t('studentDetail.documents.uploadedBy')}</th>
                  <th>{t('studentDetail.documents.size')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === 'visa-documents' ? visaDocuments : normalDocuments).map(doc => (
                  <tr key={doc.document_id}>
                    <td>
                      {doc.document_type} {doc.is_mandatory ? '\u2B50' : ''}
                      {String(doc.passport_verification_status || '') === 'verified' ? (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--success)',
                            background: 'var(--success-light)',
                            borderRadius: 999,
                            padding: '2px 8px',
                            display: 'inline-block'
                          }}
                        >
                          {t('studentDetail.documents.verifiedBadge')}
                        </span>
                      ) : null}
                    </td>
                    <td>{doc.file_name}</td>
                    <td>{new Date(doc.uploaded_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                    <td>{doc.uploaded_by_name}</td>
                    <td>{(doc.file_size / 1024).toFixed(1)} KB</td>
                    <td>
                      <div className="table-actions">
                        <button className="view" title={t('common.view')} onClick={() => handleViewDoc(doc.document_id)}><FiEye /></button>
                        <button className="edit" title={t('common.download')} onClick={() => handleDownloadDoc(doc.document_id)}><FiDownload /></button>
                        <button className="delete" title={t('common.delete')} onClick={() => handleDeleteDoc(doc.document_id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(activeTab === 'visa-documents' ? visaDocuments.length === 0 : normalDocuments.length === 0) && (
                  <tr><td colSpan="6" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>{t('studentDetail.documents.noDocuments')}</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          <HistoryDetails
            title={activeTab === 'visa-documents' ? t('studentDetail.historyTitles.visaDocs') : t('studentDetail.historyTitles.documents')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'document'}
          />
        </div>
      )}

      {/* Visa Documents access note (requires interview pass) */}
      {activeTab === 'visa-documents' && showVisaTabs && !hasPassedInterview && (
        <div className="card">
          <div className="empty-state" style={{ padding: 24 }}>
            <p style={{ color: 'var(--warning)', fontWeight: 600 }}>
              {t('studentDetail.documents.passInterviewFirst')}
            </p>
          </div>
        </div>
      )}

      {/* Fees */}
      {activeTab === 'fees' && hasPassedInterview && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3>{t('studentDetail.fees.heading')}</h3>
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => {
                setEditingFee(null);
                setFeeForm({
                  fee_type: defaultFeeType,
                  description: '',
                  currency: 'EURO',
                  amount_paid: '',
                  payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
                  payment_method: '',
                  reference_no: '',
                  payment_receipt_copy: null,
                  payment_receipt_copy_name: '',
                  notes: ''
                });
                setShowFeeModal(true);
              }}
            >
              <FiPlus /> {t('studentDetail.addFee')}
            </button>
          </div>


          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('studentDetail.fees.applicationFeeConfigured')}</div>
                <div style={{ fontWeight: 700 }}>{(configuredApplicationFee || 0).toFixed(2)}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('studentDetail.fees.paid')} {paidApplicationTotal.toFixed(2)} | {t('studentDetail.fees.remaining')} {applicationRemaining.toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('studentDetail.fees.tuitionFeeConfigured')}</div>
                <div style={{ fontWeight: 700 }}>{(configuredTuitionFee || 0).toFixed(2)}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('studentDetail.fees.scholarshipLabel')} {configuredScholarship.toFixed(2)} | {t('studentDetail.fees.netPayable')} {netTuitionPayable.toFixed(2)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('studentDetail.fees.paid')} {paidTuitionTotal.toFixed(2)} | {t('studentDetail.fees.remaining')} {tuitionRemaining.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {hasVerifiedApplicationFee && (
            <div className="card" style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{t('studentDetail.fees.enrollmentHeading')}</h4>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              {isAdminUser
                ? t('studentDetail.fees.enrollmentDesc')
                : t('studentDetail.fees.enrollmentHint')}
            </p>
            {isAdminUser ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0, flex: '1 1 220px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600 }}>{t('studentDetail.fees.enrollmentLabel')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={enrollmentInput}
                    onChange={(e) => setEnrollmentInput(e.target.value)}
                    placeholder="e.g. ENL-2026-00123"
                    autoComplete="off"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveEnrollmentNumber}
                  disabled={enrollmentSaving}
                >
                  {enrollmentSaving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 600 }}>{student.enrollment_number || '—'}</div>
            )}
          </div>
          )}

          {!hasVerifiedApplicationFee && isAdminUser && (
            <div
              className="card"
              style={{
                marginBottom: 16,
                padding: 12,
                background: 'var(--bg-secondary, #f8fafc)',
                border: '1px dashed var(--border)',
                fontSize: 13,
                color: 'var(--text-muted)'
              }}
            >
              {t('studentDetail.fees.enrollmentAfterVerify')}
            </div>
          )}

          {fees.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <p>{t('studentDetail.fees.noFees')}</p>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setEditingFee(null);
                  setFeeForm({
                    fee_type: defaultFeeType,
                    description: '',
                    currency: 'EURO',
                    amount_paid: '',
                    payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
                    payment_method: '',
                    reference_no: '',
                    payment_receipt_copy: null,
                    payment_receipt_copy_name: '',
                    notes: ''
                  });
                  setShowFeeModal(true);
                }}>
                  {t('studentDetail.addFirstFee')}
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>{t('studentDetail.fees.feeType')}</th>
                      <th>{t('studentDetail.fees.descriptionCol')}</th>
                      <th>{t('common.currency')}</th>
                      <th>{t('studentDetail.fees.amountPaid')}</th>
                      <th>{t('studentDetail.fees.verification')}</th>
                      <th>{t('studentDetail.fees.paymentDate')}</th>
                      <th>{t('studentDetail.fees.receiptCopy')}</th>
                      <th>{t('studentDetail.fees.reference')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map(fee => {
                      return (
                        <tr key={fee.fee_id}>
                          <td style={{ fontWeight: 500 }}>
                            {formatFeeTypeLabel(fee.fee_type)}
                          </td>
                          <td>{fee.description || '-'}</td>
                          <td>{fee.currency}</td>
                          <td>
                            {fee.currency} {parseFloat(fee.amount_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td>
                            <span className="badge" style={{
                              background:
                                fee.verification_status === 'verified'
                                  ? 'var(--success-light)'
                                  : fee.verification_status === 'not_verified'
                                    ? 'var(--danger-light)'
                                    : 'var(--warning-light)',
                              color:
                                fee.verification_status === 'verified'
                                  ? 'var(--success)'
                                  : fee.verification_status === 'not_verified'
                                    ? 'var(--danger)'
                                    : 'var(--warning)'
                            }}>
                              {String(fee.verification_status || 'under_verification').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            {fee.payment_date ? new Date(fee.payment_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                          </td>
                          <td style={{ fontSize: 13 }}>
                            {fee.payment_receipt_copy ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: 6,
                                    background: 'var(--primary-light)',
                                    color: 'var(--primary)',
                                    fontSize: 11,
                                    fontWeight: 600
                                  }}
                                >
                                  {t('studentDetail.fees.uploadedReceipt')}
                                </span>
                                <button
                                  className="btn btn-sm btn-outline"
                                  onClick={async () => {
                                    try {
                                      const res = await feesAPI.getReceiptUrl(fee.fee_id);
                                      window.open(res.data.url, '_blank');
                                    } catch (err) {
                                      toast.error(t('studentDetail.fees.receiptOpenFailed'));
                                    }
                                  }}
                                >
                                  <FiEye /> {t('common.view')}
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{t('studentDetail.fees.missingReceipt')}</span>
                            )}
                          </td>
                          <td style={{ fontSize: 13 }}>{fee.reference_no || '-'}</td>
                          <td>
                            <div className="table-actions">
                              <button 
                                className="edit" 
                                title={t('common.edit')} 
                                onClick={() => {
                                  setEditingFee(fee);
                                  setFeeForm({
                                    fee_type: fee.fee_type,
                                    description: fee.description || '',
                                    currency: fee.currency,
                                    amount_paid: fee.amount_paid != null ? fee.amount_paid.toString() : '',
                                    payment_date: fee.payment_date ? fee.payment_date.slice(0, 10) : '',
                                    payment_method: fee.payment_method || '',
                                    reference_no: fee.reference_no || '',
                                    payment_receipt_copy: null,
                                    payment_receipt_copy_name: getFileNameFromPath(fee.payment_receipt_copy || ''),
                                    notes: fee.notes || ''
                                  });
                                  setShowFeeModal(true);
                                }}
                              >
                                <FiEdit />
                              </button>
                              <button 
                                className="delete" 
                                title={t('common.delete')} 
                                onClick={async () => {
                                  if (window.confirm(t('studentDetail.fees.feeDeleteConfirm'))) {
                                    try {
                                      await feesAPI.delete(fee.fee_id);
                                      toast.success(t('studentDetail.fees.feeDeleted'));
                                      loadStudent();
                                    } catch (err) {
                                      toast.error(t('studentDetail.fees.feeDeleteFailed'));
                                    }
                                  }
                                }}
                              >
                                <FiTrash2 />
                              </button>
                              <button
                                type="button"
                                title={t('studentDetail.fees.generateReceiptTitle')}
                                onClick={() => {
                                  const bucket =
                                    ['tuition_fee', 'installment', 'alternance'].includes(
                                      String(fee.fee_type || '').toLowerCase()
                                    )
                                      ? 'tuition_fee'
                                      : 'application_fee';
                                  const amountLabel = `${fee.currency || ''} ${parseFloat(
                                    fee.amount_paid || 0
                                  ).toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}`.trim();
                                  const dateLabel = fee.payment_date
                                    ? new Date(fee.payment_date).toLocaleDateString('en-IN', {
                                        timeZone: 'Asia/Kolkata'
                                      })
                                    : '-';
                                  const methodLabel = fee.payment_method
                                    ? String(fee.payment_method)
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (c) => c.toUpperCase())
                                    : '-';
                                  paymentReceiptRef.current?.openForFeeRow({
                                    fee_id: fee.fee_id,
                                    fee_type: bucket,
                                    meta: { amount: amountLabel, date: dateLabel, method: methodLabel }
                                  });
                                }}
                                style={{
                                  border: '1px solid var(--primary)',
                                  background: 'var(--primary-light)',
                                  color: 'var(--primary)',
                                  borderRadius: 999,
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                <FiFileText size={12} />{' '}
                                {t('studentDetail.fees.generateReceiptShort')}
                              </button>
                              {isAdminUser && fee.verification_status === 'under_verification' && (
                                <div
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: 4,
                                    border: '1px solid var(--border-color)',
                                    borderRadius: 999,
                                    background: 'var(--bg-secondary)'
                                  }}
                                >
                                  <button
                                    type="button"
                                    title={t('studentDetail.fees.verifyFee')}
                                    onClick={() => handleFeeVerification(fee.fee_id, 'verified')}
                                    style={{
                                      border: '1px solid var(--success)',
                                      background: 'var(--success-light)',
                                      color: 'var(--success)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    <FiCheck size={12} /> {t('studentDetail.verify')}
                                  </button>
                                  <button
                                    type="button"
                                    title={t('studentDetail.fees.markNotVerified')}
                                    onClick={() => handleFeeVerification(fee.fee_id, 'not_verified')}
                                    style={{
                                      border: '1px solid var(--danger)',
                                      background: 'var(--danger-light)',
                                      color: 'var(--danger)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    <FiX size={12} /> {t('studentDetail.notVerify')}
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fees History (compact) */}
          <HistoryDetails
            title={t('studentDetail.historyTitles.fees')}
            logs={auditLog}
            filterLog={(l) => {
              const entity = String(l?.entity_type || '').toLowerCase();
              const action = String(l?.action || '').toLowerCase();
              return entity === 'fee' || action.startsWith('refund_') || action === 'mark_paid';
            }}
          />

          {/* Payment Receipts — generate and list from uploaded templates. */}
          <div className="card" style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              {t('studentDetail.fees.paymentReceiptsHeading')}
            </h4>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              {t('studentDetail.fees.paymentReceiptsHelp')}
            </p>
            <EntityDocumentGenerator
              ref={paymentReceiptRef}
              entityType="student"
              entityId={Number(id)}
              entityLabel="Student"
              campusId={student?.campus_id != null ? student.campus_id : undefined}
              templateType="payment_receipt"
            />
          </div>
        </div>
      )}

      {/* Refunds */}
      {activeTab === 'refunds' && showRefundsTab && hasPassedInterview && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3>{t('studentDetail.refunds.heading')}</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingRefund(null);
                setRefundForm({
                  refund_type: 'application_fee',
                  description: '',
                  currency: 'EURO',
                  amount_refunded: '',
                  refund_method: '',
                  reference_no: '',
                  refund_receipt_copy: null,
                  refund_receipt_copy_name: '',
                  notes: ''
                });
                setShowRefundModal(true);
              }}
            >
              <FiPlus /> Request Refund
            </button>
          </div>

          {refunds.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <p>{t('studentDetail.refunds.noRefunds')}</p>
                <button className="btn btn-primary btn-sm" onClick={() => setShowRefundModal(true)}>
                  Request Refund
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>{t('studentDetail.refunds.refundType')}</th>
                      <th>{t('common.currency')}</th>
                      <th>{t('studentDetail.refunds.amountRefunded')}</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Requested</th>
                      <th>{t('studentDetail.refunds.refundReceipt')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.map((refund) => {
                      const statusColors = {
                        pending_approval: { bg: '#fef3cd', color: '#856404' },
                        under_review: { bg: '#cce5ff', color: '#004085' },
                        approved: { bg: '#d4edda', color: '#155724' },
                        rejected: { bg: '#f8d7da', color: '#721c24' },
                        processed: { bg: '#d1ecf1', color: '#0c5460' },
                        completed: { bg: '#c3e6cb', color: '#155724' },
                        on_hold: { bg: '#e2e3e5', color: '#383d41' }
                      };
                      const sc = statusColors[refund.status] || { bg: '#e2e3e5', color: '#383d41' };
                      const priorityColors = { high: '#dc3545', medium: '#fd7e14', low: '#6c757d' };
                      const canEdit = refund.status === 'pending_approval' && Number(refund.initiated_by) === Number(currentUser?.user_id);
                      const canViewDetail = ['super_admin', 'admin', 'sales_manager'].includes(String(currentUser?.role || '').toLowerCase());
                      return (
                        <tr
                          key={refund.refund_id}
                          style={{ cursor: canViewDetail ? 'pointer' : undefined }}
                          onClick={() => { if (canViewDetail) navigate(`/refund-requests/${refund.refund_id}`); }}
                        >
                          <td style={{ fontWeight: 600, fontSize: 13, color: canViewDetail ? 'var(--primary)' : undefined }}>#{refund.refund_id}</td>
                          <td style={{ fontWeight: 500 }}>
                            {String(refund.refund_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                          </td>
                          <td>{refund.currency}</td>
                          <td style={{ fontWeight: 600 }}>
                            {refund.currency} {parseFloat(refund.amount_refunded || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td>
                            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                              {(refund.status || '').replace(/_/g, ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontWeight: 600, fontSize: 12, color: priorityColors[refund.priority] || '#6c757d' }}>
                              {(refund.priority || 'medium').toUpperCase()}
                            </span>
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {refund.created_at ? new Date(refund.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                          </td>
                          <td style={{ fontSize: 13 }}>
                            {refund.refund_receipt_copy ? (
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={async () => {
                                  try {
                                    const res = await refundsAPI.getReceiptUrl(refund.refund_id);
                                    window.open(res.data.url, '_blank');
                                  } catch (err) {
                                    toast.error(t('studentDetail.refunds.receiptOpenFailed'));
                                  }
                                }}
                              >
                                <FiEye /> {t('common.view')}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td>
                            <div className="table-actions">
                              {canViewDetail && (
                                <button
                                  className="btn btn-sm btn-outline"
                                  title="View Details"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/refund-requests/${refund.refund_id}`); }}
                                >
                                  <FiEye /> View
                                </button>
                              )}
                              {canEdit && (
                                <>
                                  <button
                                    className="edit"
                                    title={t('common.edit')}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingRefund(refund);
                                      setRefundForm({
                                        refund_type: refund.refund_type || 'application_fee',
                                        description: refund.description || '',
                                        currency: refund.currency || 'EURO',
                                        amount_refunded: refund.amount_refunded != null ? String(refund.amount_refunded) : '',
                                        refund_method: '',
                                        reference_no: '',
                                        refund_receipt_copy: null,
                                        refund_receipt_copy_name: '',
                                        notes: refund.notes || ''
                                      });
                                      setShowRefundModal(true);
                                    }}
                                  >
                                    <FiEdit />
                                  </button>
                                  <button
                                    className="delete"
                                    title={t('common.delete')}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(t('studentDetail.refunds.refundDeleteConfirm'))) return;
                                      try {
                                        await refundsAPI.delete(refund.refund_id);
                                        toast.success(t('studentDetail.refunds.refundDeleted'));
                                        loadStudent();
                                      } catch (err) {
                                        toast.error(t('studentDetail.refunds.refundDeleteFailed'));
                                      }
                                    }}
                                  >
                                    <FiTrash2 />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <HistoryDetails
            title={t('studentDetail.historyTitles.refunds')}
            logs={auditLog}
            filterLog={(l) => String(l?.action || '').toLowerCase().startsWith('refund_')}
          />
        </div>
      )}

      {/* Fees access note (requires interview pass) */}
      {activeTab === 'fees' && !hasPassedInterview && (
        <div className="card">
          <div className="empty-state" style={{ padding: 24 }}>
            <p style={{ color: 'var(--warning)', fontWeight: 600 }}>
              {t('studentDetail.fees.passInterviewFirst')}
            </p>
          </div>
        </div>
      )}

      {/* Refunds access note (requires interview pass) */}
      {activeTab === 'refunds' && showRefundsTab && !hasPassedInterview && (
        <div className="card">
          <div className="empty-state" style={{ padding: 24 }}>
            <p style={{ color: 'var(--warning)', fontWeight: 600 }}>
              {t('studentDetail.refunds.passInterviewFirst')}
            </p>
          </div>
        </div>
      )}

      {/* Interviews */}
      {activeTab === 'interviews' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3>{t('studentDetail.interviews.heading')}</h3>
            {canScheduleInterview ? (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate(`/interviews/new/${id}`, { state: { fromStudentId: id } })}
              >
                <FiPlus /> {interviews.length > 0 ? t('studentDetail.addNextInterview') : t('studentDetail.scheduleInterview')}
              </button>
            ) : (
              <div style={{
                padding: '6px 12px',
                background: 'var(--warning-light)',
                borderRadius: 'var(--radius)',
                fontSize: 12,
                color: 'var(--orange)',
                border: '1px solid var(--warning)',
                fontWeight: 500
              }}>
                {!hasUploadedRequiredDocuments
                  ? t('studentDetail.interviews.uploadDocsFirst')
                  : (mockGateRequired && student.mock_interview_enabled && !hasPassedMockInterview)
                    ? t('studentDetail.interviews.passMockFirst')
                  : hasPassedInterview
                    ? t('studentDetail.interviews.alreadyPassed')
                    : hasReachedInterviewLimit
                      ? t('studentDetail.interviews.maxThree')
                      : t('studentDetail.interviews.updatePrevious')}
              </div>
            )}
          </div>
          {interviews.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <FiVideo size={48} />
                <p>{t('studentDetail.interviews.noInterviews')}</p>
                {canScheduleInterview ? (
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/interviews/new/${id}`, { state: { fromStudentId: id } })}>
                    {t('studentDetail.scheduleFirstInterview')}
                  </button>
                ) : (
                  <p style={{ color: 'var(--warning)', fontWeight: 500, marginTop: 8 }}>
                    {!hasUploadedRequiredDocuments
                      ? t('studentDetail.interviews.uploadDocsRequired')
                      : (mockGateRequired && student.mock_interview_enabled && !hasPassedMockInterview)
                        ? t('studentDetail.interviews.passMockFirst')
                      : hasPassedInterview
                        ? t('studentDetail.interviews.studentPassedInterview')
                        : hasReachedInterviewLimit
                          ? t('studentDetail.interviews.maxThreeReached')
                          : t('studentDetail.interviews.setPreviousOutcome')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>{t('studentDetail.interviews.scheduledAt')}</th>
                      <th>{t('studentDetail.interviews.interviewLink')}</th>
                      {countryConfig.features.meetingRequired === false && <th>{t('studentDetail.interviews.mode')}</th>}
                      <th>{t('common.status')}</th>
                      <th>{t('studentDetail.interviews.outcome')}</th>
                      <th>{t('common.remarks')}</th>
                      <th>{t('studentDetail.interviews.interviewComment')}</th>
                      <th>{t('studentDetail.interviews.report')}</th>
                      <th>{t('common.createdBy')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interviews.map(interview => {
                      // Render the stored wall-clock exactly as saved (in the
                      // interview's timezone column) — NO browser/server TZ drift.
                      const scheduledDateTime = formatStoredDateTime(interview.scheduled_at);
                      
                      const getStatusColor = (status) => {
                        const colors = {
                          scheduled: 'var(--primary)',
                          completed: 'var(--success)',
                          completed_pass: 'var(--success)', // Legacy support
                          completed_fail: 'var(--success)', // Legacy support
                          cancelled: 'var(--text-muted)',
                          no_show: 'var(--warning)',
                          student_did_not_join: 'var(--danger)',
                          rescheduled: 'var(--info)'
                        };
                        return colors[status] || 'var(--text-muted)';
                      };

                      return (
                        <tr key={interview.interview_id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FiCalendar style={{ color: 'var(--text-muted)' }} />
                              <span>{scheduledDateTime}</span>
                            </div>
                          </td>
                          <td>
                            {interview.interview_link ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <a
                                  href={interview.interview_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}
                                >
                                  <FiLink /> {t('studentDetail.interviews.joinLink')}
                                </a>
                                {interview.zoom_meeting_id && (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {interview.zoom_recording_url && (
                                      <a
                                        href={interview.zoom_recording_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                      >
                                        <FiPlay size={10} /> {t('studentDetail.interviews.recording')}
                                      </a>
                                    )}
                                    {interview.zoom_transcript_url && (
                                      <a
                                        href={interview.zoom_transcript_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                      >
                                        <FiFileText size={10} /> {t('studentDetail.interviews.transcript')}
                                      </a>
                                    )}
                                    {interview.zoom_duration_minutes != null && (
                                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{interview.zoom_duration_minutes} min</span>
                                    )}
                                    <button
                                      type="button"
                                      title={t('studentDetail.interviews.syncZoom')}
                                      className="btn btn-sm btn-outline"
                                      style={{ padding: '2px 6px', fontSize: 11 }}
                                      disabled={zoomInterviewSyncingId === interview.interview_id}
                                      onClick={() => syncInterviewZoom(interview.interview_id)}
                                    >
                                      <FiRefreshCw size={11} className={zoomInterviewSyncingId === interview.interview_id ? 'spin' : ''} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (!interview.outcome || interview.outcome === 'pending') ? (
                              // Reschedule pill shown to every authenticated
                              // user when there is no link yet — the backend
                              // accepts pure reschedule payloads regardless
                              // of `interviews.update` permission.
                              <button
                                type="button"
                                title={t('studentDetail.reschedule')}
                                onClick={() => handleOpenRescheduleModal(interview)}
                                style={{
                                  border: '1px solid var(--info)',
                                  background: 'var(--info-light)',
                                  color: 'var(--info)',
                                  borderRadius: 999,
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                <FiCalendar size={12} /> {t('studentDetail.reschedule')}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          {countryConfig.features.meetingRequired === false && (
                            <td>
                              {interview.interview_mode ? (
                                <span className="badge" style={{
                                  background: interview.interview_mode === 'online' ? 'var(--info-light)' : 'var(--warning-light)',
                                  color: interview.interview_mode === 'online' ? 'var(--info)' : 'var(--warning)'
                                }}>
                                  {interview.interview_mode.charAt(0).toUpperCase() + interview.interview_mode.slice(1)}
                                </span>
                              ) : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                            </td>
                          )}
                          <td>
                            <span className="badge" style={{ 
                              background: getStatusColor(interview.status) + '20',
                              color: getStatusColor(interview.status)
                            }}>
                              {interview.status === 'completed_pass' || interview.status === 'completed_fail' 
                                ? t('studentDetail.interviews.completed') 
                                : String(interview.status || '')
                                    .replace(/_/g, ' ')
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                          </td>
                          <td>
                            {interview.outcome && interview.outcome !== 'pending' ? (
                              <span className="badge" style={{
                                background: interview.outcome === 'pass' ? 'var(--success-light)' : 'var(--danger-light)',
                                color: interview.outcome === 'pass' ? 'var(--success)' : 'var(--danger)'
                              }}>
                                {String(interview.outcome).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {interview.remarks || '-'}
                          </td>
                          <td
                            style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}
                            title={interview.interview_comment || ''}
                          >
                            {interview.interview_comment || '-'}
                          </td>
                          <td>
                            {interview.report_file_path && canPerformAction('interviews', 'read') ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline"
                                onClick={() => openInterviewReport(interview.interview_id)}
                                title={interview.report_file_name || t('studentDetail.interviews.openReport')}
                              >
                                <FiDownload size={14} /> {t('common.open')}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td style={{ fontSize: 13 }}>{interview.created_by_name || '-'}</td>
                          <td>
                            {(() => {
                              // Reschedule is available to every authenticated user.
                              // Pass/Fail stays gated on `canMarkInterviewOutcome`.
                              // Edit stays gated on `interviews.update`.
                              const showPassFail =
                                canMarkInterviewOutcome && interview.outcome === 'pending';
                              const showEdit = canPerformAction('interviews', 'update');
                              const showReschedule =
                                !interview.outcome || interview.outcome === 'pending';
                              if (!showPassFail && !showEdit && !showReschedule) {
                                return <span style={{ color: 'var(--text-muted)' }}>-</span>;
                              }
                              return (
                                <div className="table-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {showPassFail && (
                                    <div style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      padding: '4px',
                                      border: '1px solid var(--border)',
                                      borderRadius: 999,
                                      background: 'var(--bg-secondary)'
                                    }}>
                                      <button
                                        type="button"
                                        title={t('studentDetail.interviews.markPass')}
                                        onClick={() => openInterviewOutcomeOrFranceModal(interview, 'pass')}
                                        style={{
                                          border: '1px solid var(--success)',
                                          background: 'var(--success-light)',
                                          color: 'var(--success)',
                                          borderRadius: 999,
                                          padding: '4px 10px',
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}
                                      >
                                        <FiCheck size={12} /> {t('studentDetail.pass')}
                                      </button>
                                      <button
                                        type="button"
                                        title={t('studentDetail.interviews.markFail')}
                                        onClick={() => openInterviewOutcomeOrFranceModal(interview, 'fail')}
                                        style={{
                                          border: '1px solid var(--danger)',
                                          background: 'var(--danger-light)',
                                          color: 'var(--danger)',
                                          borderRadius: 999,
                                          padding: '4px 10px',
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}
                                      >
                                        <FiX size={12} /> {t('studentDetail.fail')}
                                      </button>
                                      <button
                                        type="button"
                                        title={t('interviews.markDidNotJoin')}
                                        onClick={() => openInterviewOutcomeOrFranceModal(interview, 'student_did_not_join')}
                                        style={{
                                          border: '1px solid var(--warning)',
                                          background: 'var(--warning-light)',
                                          color: 'var(--warning)',
                                          borderRadius: 999,
                                          padding: '4px 10px',
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {t('studentDetail.didNotJoin')}
                                      </button>
                                    </div>
                                  )}
                                  {showEdit && (
                                    <button
                                      className="edit"
                                      title={t('common.edit')}
                                      onClick={() => navigate(`/interviews/${interview.interview_id}`, { state: { fromStudentId: id } })}
                                    >
                                      <FiEdit />
                                    </button>
                                  )}
                                  {showReschedule && (
                                    <button
                                      type="button"
                                      title={t('studentDetail.reschedule')}
                                      onClick={() => handleOpenRescheduleModal(interview)}
                                      style={{
                                        border: '1px solid var(--info)',
                                        background: 'var(--info-light)',
                                        color: 'var(--info)',
                                        borderRadius: 999,
                                        padding: '4px 10px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4
                                      }}
                                    >
                                      <FiCalendar size={12} /> {t('studentDetail.reschedule')}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <HistoryDetails
            title={t('studentDetail.historyTitles.interviews')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'interview'}
          />
        </div>
      )}

      {/* Mock Interviews */}
      {activeTab === 'mock-interviews' && showMockInterviewsTab && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3>{t('studentDetail.mockInterviews.heading')}</h3>
            {canScheduleMockInterview ? (
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/mock-interviews/new/${id}`, { state: { fromStudentId: id } })}>
                <FiPlus /> {mockInterviews.length > 0 ? t('studentDetail.addNextMockInterview') : t('studentDetail.scheduleMockInterview')}
              </button>
            ) : (
              <div style={{
                padding: '6px 12px',
                background: 'var(--warning-light)',
                borderRadius: 'var(--radius)',
                fontSize: 12,
                color: 'var(--orange)',
                border: '1px solid var(--warning)',
                fontWeight: 500
              }}>
                {!hasUploadedRequiredDocuments
                  ? t('studentDetail.mockInterviews.uploadDocsFirst')
                  : hasPassedMockInterview
                    ? t('studentDetail.mockInterviews.alreadyPassed')
                    : hasReachedMockInterviewLimit
                      ? t('studentDetail.mockInterviews.maxThree')
                      : t('studentDetail.mockInterviews.updatePrevious')}
              </div>
            )}
          </div>
          {mockInterviews.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <FiVideo size={48} />
                <p>{t('studentDetail.mockInterviews.noMockInterviews')}</p>
                {canScheduleMockInterview ? (
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/mock-interviews/new/${id}`, { state: { fromStudentId: id } })}>
                    {t('studentDetail.scheduleFirstMockInterview')}
                  </button>
                ) : (
                  <p style={{ color: 'var(--warning)', fontWeight: 500, marginTop: 8 }}>
                    {!hasUploadedRequiredDocuments
                      ? t('studentDetail.mockInterviews.uploadDocsRequired')
                      : hasPassedMockInterview
                        ? t('studentDetail.mockInterviews.studentPassed')
                        : hasReachedMockInterviewLimit
                          ? t('studentDetail.mockInterviews.maxThreeReached')
                          : t('studentDetail.mockInterviews.setPreviousOutcome')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>{t('studentDetail.interviews.scheduledAt')}</th>
                      <th>{t('studentDetail.modals.timezone')}</th>
                      <th>{t('studentDetail.interviews.interviewLink')}</th>
                      <th>{t('common.status')}</th>
                      <th>{t('studentDetail.interviews.outcome')}</th>
                      <th>{t('common.remarks')}</th>
                      <th>{t('common.createdBy')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockInterviews.map(interview => {
                      const scheduledDateTime = formatStoredDateTime(interview.scheduled_at);

                      return (
                        <tr key={interview.mock_interview_id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FiCalendar style={{ color: 'var(--text-muted)' }} />
                              <span>{scheduledDateTime}</span>
                            </div>
                          </td>
                          <td>{interview.timezone || resolveDefaultInterviewTimezone(interviewTimezones)}</td>
                          <td>
                            {interview.interview_link ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <a
                                  href={interview.interview_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}
                                >
                                  <FiLink /> {t('studentDetail.interviews.joinLink')}
                                </a>
                                {interview.zoom_meeting_id && (
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {interview.zoom_recording_url && (
                                      <a
                                        href={interview.zoom_recording_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                      >
                                        <FiPlay size={10} /> {t('studentDetail.interviews.recording')}
                                      </a>
                                    )}
                                    {interview.zoom_transcript_url && (
                                      <a
                                        href={interview.zoom_transcript_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                      >
                                        <FiFileText size={10} /> {t('studentDetail.interviews.transcript')}
                                      </a>
                                    )}
                                    {interview.zoom_duration_minutes != null && (
                                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{interview.zoom_duration_minutes} min</span>
                                    )}
                                    <button
                                      type="button"
                                      title={t('studentDetail.mockInterviews.syncZoom')}
                                      className="btn btn-sm btn-outline"
                                      style={{ padding: '2px 6px', fontSize: 11 }}
                                      disabled={zoomMockSyncingId === interview.mock_interview_id}
                                      onClick={() => syncMockInterviewZoom(interview.mock_interview_id)}
                                    >
                                      <FiRefreshCw size={11} className={zoomMockSyncingId === interview.mock_interview_id ? 'spin' : ''} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td>
                            {interview.status
                              ? String(interview.status)
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (c) => c.toUpperCase())
                              : '-'}
                          </td>
                          <td>
                            {interview.outcome && interview.outcome !== 'pending' ? (
                              <span className="badge" style={{
                                background: interview.outcome === 'pass' ? 'var(--success-light)' : 'var(--danger-light)',
                                color: interview.outcome === 'pass' ? 'var(--success)' : 'var(--danger)'
                              }}>
                                {String(interview.outcome).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {interview.remarks || '-'}
                          </td>
                          <td style={{ fontSize: 13 }}>{interview.created_by_name || '-'}</td>
                          <td>
                            <div className="table-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {canMarkInterviewOutcome && interview.outcome === 'pending' && (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  padding: '4px',
                                  border: '1px solid var(--border)',
                                  borderRadius: 999,
                                  background: 'var(--bg-secondary)'
                                }}>
                                  <button
                                    type="button"
                                    title={t('studentDetail.mockInterviews.markPass')}
                                    onClick={() => handleMockInterviewOutcomeUpdate(interview.mock_interview_id, 'pass')}
                                    style={{
                                      border: '1px solid var(--success)',
                                      background: 'var(--success-light)',
                                      color: 'var(--success)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    <FiCheck size={12} /> {t('studentDetail.pass')}
                                  </button>
                                  <button
                                    type="button"
                                    title={t('studentDetail.interviews.markFail')}
                                    onClick={() => handleMockInterviewOutcomeUpdate(interview.mock_interview_id, 'fail')}
                                    style={{
                                      border: '1px solid var(--danger)',
                                      background: 'var(--danger-light)',
                                      color: 'var(--danger)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    <FiX size={12} /> {t('studentDetail.fail')}
                                  </button>
                                  <button
                                    type="button"
                                    title={t('interviews.markDidNotJoin')}
                                    onClick={() => handleMockInterviewOutcomeUpdate(interview.mock_interview_id, 'student_did_not_join')}
                                    style={{
                                      border: '1px solid var(--warning)',
                                      background: 'var(--warning-light)',
                                      color: 'var(--warning)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {t('studentDetail.didNotJoin')}
                                  </button>
                                </div>
                              )}
                              <button className="edit" title={t('common.edit')} onClick={() => navigate(`/mock-interviews/${interview.mock_interview_id}`, { state: { fromStudentId: id } })}>
                                <FiEdit />
                              </button>
                              {(!interview.outcome || interview.outcome === 'pending') && (
                                <button
                                  type="button"
                                  title={t('studentDetail.reschedule')}
                                  onClick={() => handleOpenMockRescheduleModal(interview)}
                                  style={{
                                    border: '1px solid var(--info)',
                                    background: 'var(--info-light)',
                                    color: 'var(--info)',
                                    borderRadius: 999,
                                    padding: '4px 10px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}
                                >
                                  <FiCalendar size={12} /> {t('studentDetail.reschedule')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <HistoryDetails
            title={t('studentDetail.historyTitles.mockInterviews')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'interview'}
          />
        </div>
      )}

      {/* Activities */}
      {activeTab === 'activities' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>{t('studentDetail.logActivity')}</button>
          </div>
          <div className="timeline">
            {activities.map(a => (
              <div className={`timeline-item ${a.phase === 'pre-conversion' ? 'pre-conversion' : ''}`} key={a.activity_id}>
                {a.phase === 'pre-conversion' && <div style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 600, marginBottom: 4 }}>{t('studentDetail.activities.preConversion')}</div>}
                <div className="timeline-header">
                  <div className="timeline-type"><StatusBadge status={a.activity_type} /></div>
                  <div className="timeline-date">{new Date(a.activity_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} {a.activity_time}</div>
                </div>
                <div className="timeline-notes">{a.notes}</div>
                <div className="timeline-meta">
                  {a.next_followup && <span>{t('studentDetail.activities.nextLabel')} {new Date(a.next_followup).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>}
                  <span>{t('studentDetail.activities.byLabel')} {a.created_by_name}</span>
                </div>
              </div>
            ))}
            {activities.length === 0 && <div className="empty-state"><p>{t('studentDetail.activities.noActivities')}</p></div>}
          </div>

          <HistoryDetails
            title={t('studentDetail.historyTitles.activities')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'activity'}
          />
        </div>
      )}

      {activeTab === 'emails' && canViewEmailsTab && (
        <div>
          <EmailThreadPanel
            entityType="student"
            entityId={id}
            entityEmail={student?.email}
            defaultCc={isB2BStudent ? (student?.agency_email || '') : ''}
            allowSending={studentEmailComposeFlags.allowSending}
            useParentSendPermissionOnly={studentEmailComposeFlags.useParentSendPermissionOnly}
          />
          <HistoryDetails
            title={t('studentDetail.historyTitles.emails')}
            logs={auditLog}
            filterLog={(l) => String(l?.action || '').toLowerCase().startsWith('email_')}
          />
        </div>
      )}

      {/* Meetings */}
      {activeTab === 'meetings' && showMeetingsTab && (
        <div>
          {meetings.map(m => (
            <div className="card" key={m.meeting_id} style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, marginBottom: 4, fontWeight: 700 }}>{m.title}</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span className="badge badge-new">{m.meeting_type}</span>
                <span className="badge badge-followup">{m.mode}</span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} at {m.meeting_time}
                {m.location && ` | ${m.location}`}
              </p>
              {m.notes && <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>{m.notes}</p>}
              {m.outcome && <p style={{ marginTop: 4, fontWeight: 600, color: 'var(--success)' }}>{t('studentDetail.meetings.outcomeLabel')} {m.outcome}</p>}
            </div>
          ))}
          {meetings.length === 0 && <div className="empty-state"><p>{t('studentDetail.meetings.noMeetings')}</p></div>}

          <HistoryDetails
            title={t('studentDetail.historyTitles.meetings')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'meeting'}
          />
        </div>
      )}

      {activeTab === 'jobs' && showJobsTab && (
        <div className="card">
          <h3>{t('jobs.title', 'Jobs')}</h3>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>{t('jobs.company', 'Company')}</th>
                  <th>{t('jobs.offerTitle', 'Job Title')}</th>
                  <th>{t('jobs.jobType', 'Job Type')}</th>
                  <th>{t('jobs.contractType', 'Contract Type')}</th>
                  <th>{t('settings.fields.programType', 'Program Type')}</th>
                  <th>{t('studentDetail.personal.status', 'Status')}</th>
                  <th>{t('jobs.assignedManager', 'Handled By')}</th>
                  <th>{t('common.createdAt', 'Assigned At')}</th>
                </tr>
              </thead>
              <tbody>
                {(jobOpenings || []).map((j) => (
                  <tr key={j.job_student_link_id || `${j.job_id}-${j.assigned_at}`}>
                    <td>
                      {j.company_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => navigate(`/companies/${j.company_id}`)}
                        >
                          {j.company_name || '-'}
                        </button>
                      ) : (j.company_name || '-')}
                    </td>
                    <td>
                      {j.job_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => navigate(`/jobs/${j.job_id}`)}
                        >
                          {j.offer_title || j.job_title || '-'}
                        </button>
                      ) : (j.offer_title || j.job_title || '-')}
                    </td>
                    <td>{j.job_type || '-'}</td>
                    <td>{j.contract_type || '-'}</td>
                    <td>{j.program_type || 'All'}</td>
                    <td><StatusBadge status={j.job_student_status || '-'} /></td>
                    <td>{j.assigned_manager_name || j.handover_manager_name || '-'}</td>
                    <td>{j.assigned_at ? new Date(j.assigned_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                  </tr>
                ))}
                {(jobOpenings || []).length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>
                      {t('jobs.noJobs', 'No job openings found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lead History */}
      {activeTab === 'lead-history' && (
        <div className="card">
          {student.lead_ref ? (
            <>
              <h3 style={{ marginBottom: 12, fontWeight: 700 }}>{t('studentDetail.leadHistoryTab.originalLead')} {student.lead_ref}</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('studentDetail.leadHistoryTab.convertedInfo')}</p>
              <button className="btn btn-outline btn-sm" onClick={() => navigate(`/b2b-agencies/${student.original_lead_id}`)}>{t('studentDetail.leadHistoryTab.viewFullAgency')}</button>
            </>
          ) : (
            <div className="empty-state"><p>{t('studentDetail.leadHistoryTab.directEntry')}</p></div>
          )}

          <HistoryDetails
            title={t('studentDetail.historyTitles.lead')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'lead'}
          />
        </div>
      )}

      {/* Letters Tab */}
      {activeTab === 'letters' && (
        <div>
          <EntityDocumentGenerator
            entityType="student"
            entityId={Number(id)}
            entityLabel="Student"
            campusId={student?.campus_id != null ? student.campus_id : undefined}
            hasVerifiedApplicationFee={hasVerifiedApplicationFee}
            hasVerifiedTuitionFee={hasVerifiedTuitionFee}
          />
        </div>
      )}

      {/* Audit Log */}
      {activeTab === 'audit' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 0' }}>
            <AuditTimeline logs={auditLog} showEntity />
          </div>
        </div>
      )}

      <FranceInterviewOutcomeModal
        open={!!franceInterviewOutcomeDialog}
        interview={franceInterviewOutcomeDialog?.interview}
        targetOutcome={franceInterviewOutcomeDialog?.outcome}
        onClose={() => setFranceInterviewOutcomeDialog(null)}
        onSuccess={() => loadStudent()}
      />

      {/* Activity Modal */}
      <Modal isOpen={showActivityModal} onClose={() => setShowActivityModal(false)} title={t('studentDetail.modals.logActivity')}
        footer={<><button className="btn btn-outline" onClick={() => setShowActivityModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={handleLogActivity}>{t('common.save')}</button></>}>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('studentDetail.modals.activityType')} <span className="required">*</span></label>
            <select value={activityForm.activity_type} onChange={(e) => setActivityForm(f => ({ ...f, activity_type: e.target.value }))}>
              {['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'].map(at => <option key={at} value={at}>{t(`common.activityType.${at}`, { defaultValue: at })}</option>)}
            </select>
          </div>
          <div className="form-group"><label>{t('common.date')} <span className="required">*</span></label>
            <input type="date" value={activityForm.activity_date} onChange={(e) => setActivityForm(f => ({ ...f, activity_date: e.target.value }))} /></div>
          <div className="form-group"><label>{t('common.time')}</label>
            <input type="time" value={activityForm.activity_time} onChange={(e) => setActivityForm(f => ({ ...f, activity_time: e.target.value }))} /></div>
          <div className="form-group"><label>{t('studentDetail.modals.nextFollowup')}</label>
            <input type="date" value={activityForm.next_followup} onChange={(e) => setActivityForm(f => ({ ...f, next_followup: e.target.value }))} /></div>
          <div className="form-group full-width"><label>{t('common.notes')} <span className="required">*</span></label>
            <textarea value={activityForm.notes} onChange={(e) => setActivityForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('studentDetail.modals.describeInteraction')} /></div>
        </div>
      </Modal>

      {/* Reschedule Mock Interview Modal */}
      <Modal
        isOpen={showMockRescheduleModal}
        onClose={() => {
          if (mockRescheduleLoading) return;
          setShowMockRescheduleModal(false);
          setReschedulingMockInterview(null);
        }}
        title={t('studentDetail.modals.rescheduleMock')}
        footer={
          <>
            <button
              className="btn btn-outline"
              disabled={mockRescheduleLoading}
              onClick={() => {
                setShowMockRescheduleModal(false);
                setReschedulingMockInterview(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={mockRescheduleLoading} onClick={handleRescheduleMockInterview}>
              {mockRescheduleLoading ? t('studentDetail.updatingBtn') : t('studentDetail.updateReschedule')}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('common.date')} <span className="required">*</span></label>
            <input
              type="date"
              value={mockRescheduleSlotDate}
              min={getTodayDateInputMin()}
              onChange={(e) => {
                setMockRescheduleSlotDate(e.target.value);
                setMockRescheduleSlotId('');
                setDesiredMockRescheduleSlotTime('');
              }}
            />
          </div>
          <div className="form-group">
            <label>{t('studentDetail.modals.slot')} <span className="required">*</span></label>
            <select
              value={mockRescheduleSlotId}
              onChange={(e) => setMockRescheduleSlotId(e.target.value)}
              disabled={!mockRescheduleSlotDate || mockRescheduleSlotsLoading}
            >
              <option value="">
                {mockRescheduleSlotsLoading ? t('studentDetail.modals.loadingSlots') : (mockRescheduleSlotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
              </option>
              {mockRescheduleSlotOptions.map((s) => {
                const start = String(s.start_time || '').slice(0, 5);
                const end = String(s.end_time || '').slice(0, 5);
                const label = s.label || `${start} - ${end}`;
                const isFull = !!s.is_full;
                const bypass = canBypassInterviewSlotCapacity(currentUser);
                const disabled = !bypass && isFull;
                const capText = formatInterviewSlotAvailabilitySuffix(s);
                return (
                  <option key={s.slot_id} value={String(s.slot_id)} disabled={disabled}>
                    {label} — {capText}{disabled ? ' (Full)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-group">
            <label>{t('studentDetail.modals.timezone')} <span className="required">*</span></label>
            <select
              value={rescheduleForm.timezone}
              onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))}
            >
              {interviewTimezones.length > 0 ? (
                interviewTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_name}>
                    {tz.timezone_label || tz.timezone_name}
                  </option>
                ))
              ) : (
                <option value={getBrowserIanaTimezone()}>{getBrowserIanaTimezone()} {t('studentDetail.modals.local')}</option>
              )}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('studentDetail.modals.interviewLink')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="url"
                value={rescheduleForm.interview_link}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, interview_link: e.target.value }))}
                placeholder={t('studentDetail.modals.zoomPlaceholder')}
                style={{ flex: 1 }}
              />
              {zoomConfigured && rescheduleForm.scheduled_at && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                  disabled={zoomMockGenerating}
                  onClick={async () => {
                    setZoomMockGenerating(true);
                    try {
                      const { data } = await zoomAPI.generate({
                        topic: `Mock Interview - ${reschedulingMockInterview?.first_name || student?.first_name || 'Student'}`,
                        start_time: rescheduleForm.scheduled_at,
                        duration: 60,
                        timezone: rescheduleForm.timezone || resolveDefaultInterviewTimezone(interviewTimezones),
                        entity_type: 'mock_interview',
                        entity_id: reschedulingMockInterview?.mock_interview_id || undefined
                      });
                      setRescheduleForm((f) => ({ ...f, interview_link: data.join_url || f.interview_link }));
                      toast.success(t('interviews.zoomGenerated') || t('mockInterviews.zoomGenerated'));
                    } catch (err) {
                      toast.error(err.response?.data?.message || t('interviews.zoomFailed') || t('mockInterviews.zoomFailed'));
                    }
                    setZoomMockGenerating(false);
                  }}
                >
                  <FiVideo size={14} /> {zoomMockGenerating ? t('interviews.creatingZoom') : t('interviews.generateZoom')}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title={t('studentDetail.modals.uploadDocument')}
        footer={<><button className="btn btn-outline" disabled={uploadingDocument} onClick={() => setShowUploadModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" disabled={uploadingDocument} onClick={handleUpload}>{uploadingDocument ? t('common.uploading') : t('common.upload')}</button></>}>
        <div>
          <div className="form-group">
            <label>{t('studentDetail.modals.docType')} <span className="required">*</span></label>
            <select value={uploadForm.document_type} onChange={(e) => setUploadForm(f => ({ ...f, document_type: e.target.value, custom_document_type: '' }))}>
              {uploadTypeOptions.map((opt) => (
                <option key={`${opt.category}-${opt.document_type_id}`} value={opt.document_type_name}>
                  {opt.document_type_name} {opt.is_required ? '\u2B50 Required' : ''}
                </option>
              ))}
            </select>
          </div>
          {isOtherDocumentType(uploadForm.document_type) && (
            <div className="form-group">
              <label>{t('studentDetail.modals.docType')} ({t('common.other')}) <span className="required">*</span></label>
              <input
                type="text"
                value={uploadForm.custom_document_type || ''}
                onChange={(e) => setUploadForm((f) => ({ ...f, custom_document_type: e.target.value }))}
                placeholder="Enter document name"
              />
            </div>
          )}
          <div className="form-group">
            <label>{t('studentDetail.modals.file')} <span className="required">*</span></label>
            <input type="file" accept=".pdf,.jpg,.jpeg"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f) {
                  const err = getDocumentFileSizeError(f);
                  if (err) { toast.error(err); e.target.value = ''; return; }
                }
                setUploadForm((prev) => ({ ...prev, file: f }));
              }}
              style={{ padding: '8px 0' }} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{t('studentDetail.documents.maxFileSize')}</div>
          </div>
          <div className="form-group">
            <label>{t('common.notes')}</label>
            <textarea value={uploadForm.notes} onChange={(e) => setUploadForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('studentDetail.modals.optionalNotes')} />
          </div>
        </div>
      </Modal>

      {/* Reschedule Interview Modal */}
      <Modal
        isOpen={showRescheduleModal}
        onClose={() => {
          if (rescheduleLoading) return;
          setShowRescheduleModal(false);
          setReschedulingInterview(null);
        }}
        title={t('studentDetail.modals.rescheduleInterview')}
        footer={
          <>
            <button
              className="btn btn-outline"
              disabled={rescheduleLoading}
              onClick={() => {
                setShowRescheduleModal(false);
                setReschedulingInterview(null);
              }}
            >
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={rescheduleLoading} onClick={handleRescheduleInterview}>
              {rescheduleLoading ? t('studentDetail.updatingBtn') : t('studentDetail.updateReschedule')}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('common.date')} <span className="required">*</span></label>
            <input
              type="date"
              value={rescheduleSlotDate}
              min={getTodayDateInputMin()}
              onChange={(e) => {
                setRescheduleSlotDate(e.target.value);
                setRescheduleSlotId('');
                setDesiredRescheduleSlotTime('');
              }}
            />
          </div>
          <div className="form-group">
            <label>{t('studentDetail.modals.slot')} <span className="required">*</span></label>
            <select
              value={rescheduleSlotId}
              onChange={(e) => setRescheduleSlotId(e.target.value)}
              disabled={!rescheduleSlotDate || rescheduleSlotsLoading}
            >
              <option value="">
                {rescheduleSlotsLoading ? t('studentDetail.modals.loadingSlots') : (rescheduleSlotOptions.length ? t('studentDetail.modals.selectSlot') : t('studentDetail.modals.noSlotsConfigured'))}
              </option>
              {rescheduleSlotOptions.map((s) => {
                const start = String(s.start_time || '').slice(0, 5);
                const end = String(s.end_time || '').slice(0, 5);
                const label = s.label || `${start} - ${end}`;
                const isFull = !!s.is_full;
                const bypass = canBypassInterviewSlotCapacity(currentUser);
                const disabled = !bypass && isFull;
                const capText = formatInterviewSlotAvailabilitySuffix(s);
                return (
                  <option key={s.slot_id} value={String(s.slot_id)} disabled={disabled}>
                    {label} — {capText}{disabled ? ' (Full)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="form-group">
            <label>{t('studentDetail.modals.timezone')} <span className="required">*</span></label>
            <select
              value={rescheduleForm.timezone}
              onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))}
            >
              {interviewTimezones.length > 0 ? (
                interviewTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_name}>
                    {tz.timezone_label || tz.timezone_name}
                  </option>
                ))
              ) : (
                <option value={getBrowserIanaTimezone()}>{getBrowserIanaTimezone()} {t('studentDetail.modals.local')}</option>
              )}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('studentDetail.modals.interviewLink')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="url"
                value={rescheduleForm.interview_link}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, interview_link: e.target.value }))}
                placeholder={t('studentDetail.modals.zoomPlaceholder')}
                style={{ flex: 1 }}
              />
              {zoomConfigured && rescheduleForm.scheduled_at && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                  disabled={zoomInterviewGenerating}
                  onClick={async () => {
                    setZoomInterviewGenerating(true);
                    try {
                      const { data } = await zoomAPI.generate({
                        topic: `Interview - ${student?.first_name || 'Student'}`,
                        start_time: rescheduleForm.scheduled_at,
                        duration: 60,
                        timezone: rescheduleForm.timezone || resolveDefaultInterviewTimezone(interviewTimezones),
                        entity_type: 'interview',
                        entity_id: reschedulingInterview?.interview_id || undefined
                      });
                      setRescheduleForm((f) => ({ ...f, interview_link: data.join_url || f.interview_link }));
                      toast.success(t('interviews.zoomGenerated'));
                    } catch (err) {
                      toast.error(err.response?.data?.message || t('interviews.zoomFailed'));
                    }
                    setZoomInterviewGenerating(false);
                  }}
                >
                  <FiVideo size={14} /> {zoomInterviewGenerating ? t('interviews.creatingZoom') : t('interviews.generateZoom')}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Fee Modal */}
      <Modal 
        isOpen={showFeeModal} 
        onClose={() => {
          setShowFeeModal(false);
          setEditingFee(null);
          setFeeForm({
            fee_type: defaultFeeType,
            description: '',
            currency: 'EURO',
            amount_paid: '',
            payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
            payment_method: '',
            reference_no: '',
            payment_receipt_copy: null,
            payment_receipt_copy_name: '',
            notes: ''
          });
        }} 
        title={editingFee ? t('studentDetail.modals.editFee') : t('studentDetail.modals.addFee')}
        footer={
          <>
            <button className="btn btn-outline" onClick={() => {
              if (feeSaving) return;
              setShowFeeModal(false);
              setEditingFee(null);
              setFeeForm({
                fee_type: defaultFeeType,
                description: '',
                currency: 'EURO',
                amount_paid: '',
                payment_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
                payment_method: '',
                reference_no: '',
                payment_receipt_copy: null,
                payment_receipt_copy_name: '',
                notes: ''
              });
            }}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={feeSaving} onClick={handleSaveFee}>
              {feeSaving ? t('common.saving') : `${editingFee ? t('common.update') : t('studentDetail.addFee')}`}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('studentDetail.modals.feeType')} <span className="required">*</span></label>
            <select
              value={feeForm.fee_type}
              onChange={(e) => setFeeForm({ ...feeForm, fee_type: e.target.value })}
              required
            >
              {feeTypeSelectOptions.map((ft) => (
                <option key={ft} value={ft}>
                  {formatFeeTypeLabel(ft)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('common.currency')} <span className="required">*</span></label>
            <select
              value={feeForm.currency}
              onChange={(e) => setFeeForm({ ...feeForm, currency: e.target.value })}
              required
            >
              <option value="EURO">{t('common.currencyEuro', 'EURO (Euro)')}</option>
              <option value="AED">{t('common.currencyAed', 'AED (United Arab Emirates Dirham)')}</option>
         
            </select>
          </div>

          <div className="form-group">
            <label>{t('studentDetail.modals.amountPaid')} <span className="required">*</span></label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={feeForm.amount_paid}
              onChange={(e) => setFeeForm({ ...feeForm, amount_paid: e.target.value })}
              placeholder="0.00"
              required
            />
          </div>

          <div className="form-group">
            <label>{t('studentDetail.modals.paymentDate')} <span className="required">*</span></label>
            <input
              type="date"
              value={feeForm.payment_date}
              onChange={(e) => setFeeForm({ ...feeForm, payment_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('studentDetail.modals.paymentMethod')}</label>
            <select
              value={feeForm.payment_method}
              onChange={(e) => setFeeForm({ ...feeForm, payment_method: e.target.value })}
            >
              <option value="">{t('studentDetail.modals.selectMethod')}</option>
              <option value="fly_wire">{t('studentDetail.modals.flywire')}</option>
              <option value="bank_transfer">{t('studentDetail.modals.bankTransfer')}</option>
              <option value="instalment">{t('studentDetail.modals.instalment')}</option>
              <option value="other">{t('common.other')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('studentDetail.modals.referenceNumber')}</label>
            <input
              type="text"
              value={feeForm.reference_no}
              onChange={(e) => setFeeForm({ ...feeForm, reference_no: e.target.value })}
              placeholder={t('studentDetail.modals.receiptRef')}
            />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('studentDetail.modals.paymentReceipt')} <span className="required">*</span></label>
            <div
              style={{
                border: '1px dashed var(--border-color)',
                borderRadius: 8,
                padding: 12,
                background: 'var(--card-bg)'
              }}
            >
              <input
                id="fee-receipt-file"
                type="file"
                accept=".pdf,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f) {
                    const err = getDocumentFileSizeError(f);
                    if (err) { toast.error(err); e.target.value = ''; return; }
                  }
                  setFeeForm((prev) => ({
                    ...prev,
                    payment_receipt_copy: f,
                    payment_receipt_copy_name: f?.name || prev.payment_receipt_copy_name
                  }));
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {feeForm.payment_receipt_copy_name || t('common.noFileSelected')}
                  </div>
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('studentDetail.documents.allowedFormats')}
                  </small>
                </div>
                <label htmlFor="fee-receipt-file" className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                  <FiUpload /> {feeForm.payment_receipt_copy_name ? t('common.changeFile') : t('common.chooseFile')}
                </label>
              </div>
            </div>
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('common.description')}</label>
            <input
              type="text"
              value={feeForm.description}
              onChange={(e) => setFeeForm({ ...feeForm, description: e.target.value })}
              placeholder={t('studentDetail.modals.feeDescription')}
            />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('common.notes')}</label>
            <textarea
              value={feeForm.notes}
              onChange={(e) => setFeeForm({ ...feeForm, notes: e.target.value })}
              rows={4}
              placeholder={t('studentDetail.modals.feeNotes')}
            />
          </div>
        </div>
      </Modal>

      {/* Refund Request Modal */}
      <Modal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setEditingRefund(null);
          setRefundForm({ refund_type: 'application_fee', description: '', currency: 'EURO', amount_refunded: '', refund_method: '', reference_no: '', refund_receipt_copy: null, refund_receipt_copy_name: '', notes: '' });
        }}
        title={editingRefund ? t('studentDetail.modals.editRefund') : 'Submit Refund Request'}
        footer={
          <>
            <button
              className="btn btn-outline"
              onClick={() => {
                if (refundSaving) return;
                setShowRefundModal(false);
                setEditingRefund(null);
                setRefundForm({ refund_type: 'application_fee', description: '', currency: 'EURO', amount_refunded: '', refund_method: '', reference_no: '', refund_receipt_copy: null, refund_receipt_copy_name: '', notes: '' });
              }}
            >
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={refundSaving} onClick={handleSaveRefund}>
              {refundSaving ? t('common.saving') : (editingRefund ? t('common.update') : 'Submit Request')}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('studentDetail.modals.refundType')} <span className="required">*</span></label>
            <select value={refundForm.refund_type} onChange={(e) => setRefundForm({ ...refundForm, refund_type: e.target.value })} required>
              <option value="application_fee">{t('studentDetail.modals.applicationFee')}</option>
              <option value="tuition_fee">{t('studentDetail.modals.tuitionFee')}</option>
              <option value="registration_fee">{t('studentDetail.modals.registrationFee')}</option>
              <option value="visa_fee">{t('studentDetail.modals.visaFee')}</option>
              <option value="other">{t('common.other')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('common.currency')}</label>
            <select value={refundForm.currency} onChange={(e) => setRefundForm({ ...refundForm, currency: e.target.value })}>
              <option value="EURO">{t('common.currencyEuro', 'EURO (Euro)')}</option>
              <option value="AED">{t('common.currencyAed', 'AED (United Arab Emirates Dirham)')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('studentDetail.modals.amountRefunded')} <span className="required">*</span></label>
            <input type="number" step="0.01" min="0.01" value={refundForm.amount_refunded} onChange={(e) => setRefundForm({ ...refundForm, amount_refunded: e.target.value })} placeholder="0.00" required />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('common.description')}</label>
            <input type="text" value={refundForm.description} onChange={(e) => setRefundForm({ ...refundForm, description: e.target.value })} placeholder="Brief description of refund reason" />
          </div>

          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('common.notes')}</label>
            <textarea value={refundForm.notes} onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })} rows={3} placeholder="Additional notes (optional)" />
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          This request will be sent for approval to the management team.
        </p>
      </Modal>
    </div>
  );
};

export default StudentDetail;
